import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { 
  Building, MapPin, CreditCard, User, Check, AlertCircle, 
  Loader2, ChevronRight, ChevronLeft, ExternalLink, AlertTriangle,
  RefreshCw, ShieldCheck, Crown, Package, Zap, Clock, Store,
  BadgeCheck, Shield, Upload, Info, Save
} from 'lucide-react';
import { clearOnboardingArtifactsCache } from '../hooks/useOnboardingArtifacts';
import OnboardingHeader from '../components/OnboardingHeader';

const VendorTaxRegistration = () => {
  const { toast } = useToast();
  const { user, apiCall, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Onboarding profile from wizard
  const [onboardingProfile, setOnboardingProfile] = useState(null);
  
  // Check if this is an upgrade flow (from Profile page)
  const isUpgradeMode = searchParams.get('upgrade') === 'gst';

  // Fetch onboarding profile on mount
  useEffect(() => {
    if (user?.onboarding_profile) {
      setOnboardingProfile(user.onboarding_profile);
    }
  }, [user]);

  // iter-204: ALWAYS capture an address. For vendors who don't sell products
  // (services/rentals/events only) this step is labeled "Business Address"
  // instead of "Pickup Address" but it's still required — data is needed for
  // GST invoicing, compliance filings, payment-gateway KYC, and general
  // admin-side address lookup.
  const hasProductIntent = !onboardingProfile?.intents?.length
    || onboardingProfile.intents.includes('products');
  const shouldSkipPickupAddress = false;  // Never skip; rename-only for non-product vendors
  const isPickupAddressStep = hasProductIntent;  // true → "Pickup Address", false → "Business Address"

  // Track if vendor is returning to review (already completed onboarding)
  const isReviewMode = !isUpgradeMode && user?.onboarding_completed === true;

  // iter-230ab — Onboarding completion is NOT the same as
  // `registration_step >= 6`. The latter just means "the vendor reached
  // the tax-registration step counter"; bank/KYC/subscription can still
  // be pending. The previous heuristic bounced half-onboarded vendors
  // back to /dashboard, where PrivateRoute promptly redirected them to
  // /vendor/tax-registration → /dashboard → … infinite ping-pong.
  // Only redirect when onboarding is GENUINELY complete (single
  // authoritative flag, set by backend on the final step), which is
  // already covered by `isReviewMode` above.
  useEffect(() => {
    // Skip redirect if in upgrade mode or review mode (review-mode
    // means the vendor is genuinely onboarded and revisiting their
    // tax data — the route guard handles the actual navigation).
    if (isUpgradeMode || isReviewMode) {
      return;
    }
    // No legacy `registration_step >= 6` redirect anymore. If the vendor
    // resumed mid-flow, let the form render the step they're on so they
    // can complete it; PrivateRoute will route them to the correct next
    // step (via artifacts.next_step_route) only when truly complete.
  }, [isUpgradeMode, isReviewMode]);

  // CAPTCHA state for GST verification
  const [captchaData, setCaptchaData] = useState({
    sessionId: null,
    captchaImage: null,
    captchaInput: '',
    loading: false,
    showCaptcha: false
  });
  
  // CAPTCHA state for Enrollment ID verification
  const [enrollmentCaptchaData, setEnrollmentCaptchaData] = useState({
    sessionId: null,
    captchaImage: null,
    captchaInput: '',
    loading: false,
    showCaptcha: false
  });
  
  // Enrollment verification result
  const [enrollmentVerificationResult, setEnrollmentVerificationResult] = useState(null);

  // Auto-save key for localStorage
  const AUTO_SAVE_KEY = `tax_registration_draft_${user?.id || 'anonymous'}`;
  
  // Load saved draft on mount (returns { data, step } or null)
  const loadSavedDraft = () => {
    try {
      const saved = localStorage.getItem(AUTO_SAVE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only load if saved within last 7 days
        if (parsed.savedAt && (Date.now() - parsed.savedAt) < 7 * 24 * 60 * 60 * 1000) {
          return { data: parsed.data, step: parsed.step || 1 };
        }
      }
    } catch (e) {
      console.error('Error loading draft:', e);
    }
    return null;
  };
  
  // Get initial form data (from draft or defaults)
  const getInitialFormData = () => {
    const draft = loadSavedDraft();
    const savedDraft = draft?.data;
    const defaults = {
      // Business Details (Step 1)
      hasGST: 'yes',
      gstin: '',
      gstVerified: false,
      gstLegalName: '',
      gstTradeName: '',
      gstAdditionalTradeNames: [],
      gstBusinessAddress: '',
      gstParsedAddress: null,
      gstStatus: '',
      gstRegistrationDate: '',
      gstConstitution: '',
      gstTaxpayerType: '',
      gstCoreBusinessActivity: '',
      gstNatureOfBusiness: [],
      gstStateJurisdiction: '',
      gstCenterJurisdiction: '',
      gstStateJurisdictionDetails: null,
      gstCenterJurisdictionDetails: null,
      gstEinvoiceStatus: '',
      gstEinvoiceEnabled: false,
      gstAadhaarValidated: '',
      gstAadhaarValidationDate: '',
      gstEkycVerified: '',
      gstFieldVisitConducted: '',
      gstPrincipalAddress: null,
      gstHsnCodes: [],
      gstSacCodes: [],
      gstHasGoodsRegistration: false,
      gstHasServicesRegistration: false,
      panNumber: '',
      panValid: false,
      // iter-233-F — Personal PAN: director/proprietor's individual PAN
      // (4th char = P). REQUIRED for Tier-3 & Tier-4 (GST users) so
      // Razorpay Stakeholder kyc.pan can be set. For sole proprietors
      // this may be the same as the business PAN.
      personalPan: '',
      personalPanValid: false,
      // iter-233-F — IEC Code: Import-Export Code from DGFT. Optional;
      // presence promotes vendor from Tier-3 → Tier-4 (international
      // shipping eligible).
      iecCode: '',
      businessName: '',
      businessType: 'individual',
      // Canonical SSOT entity type + turnover (utils/vendor_enums.py)
      companyType: '',
      annualTurnover: '',
      enrollmentId: '',
      enrollmentVerified: false,
      applyEnrollmentLater: false,
      
      // Pickup Address (Step 2)
      useGSTAddress: true,
      addressPrefilled: false,
      roomNumber: '',
      streetLocality: '',
      landmark: '',
      pincode: '',
      city: '',
      state: '',
      // iter-229f — Delivery co-ordinator contact no. — the rider calls
      // this number when arriving for pickup. Defaults to the vendor's
      // own mobile during onboarding; vendor can override per-pickup.
      deliveryCoordinatorPhone: '',
      
      // Bank Details (Step 3)
      accountNumber: '',
      confirmAccountNumber: '',
      ifscCode: '',
      bankVerified: false,
      bankName: '',
      branch: '',
      bankAddress: '',
      accountHolderName: '',
      
      // Supplier Details (Step 4) - Mobile added, storeName removed
      fullName: '',
      mobileNumber: user?.mobile || '',
      whatsappNumber: '',
      whatsappOptIn: false,
      emailId: user?.email || '',
      agreeToTerms: false,
      whatsappCommunicationsOptIn: true,
      // Tier 5 — GST Exempt
      gstExemptCategory: '',
      gstExemptReason: '',
      gstExemptDeclared: false
    };
    
    // Merge saved draft with defaults (draft takes precedence)
    if (savedDraft) {
      return { ...defaults, ...savedDraft };
    }
    return defaults;
  };
  
  const [formData, setFormData] = useState(getInitialFormData);
  const savedDraftRef = loadSavedDraft();
  const [hasDraft, setHasDraft] = useState(!!savedDraftRef);

  // Restore step from localStorage draft on mount (only for in-progress registration)
  useEffect(() => {
    // If user already completed onboarding and is returning to review, start at step 1
    if (isReviewMode) return;
    const draft = loadSavedDraft();
    if (draft?.step && draft.step > 1) {
      setCurrentStep(draft.step);
    }
  }, []); // Run once on mount

  // Hydrate from backend if no localStorage draft exists.
  // iter-230j (v2) — Guard with a hydratedForUserRef so even under React
  // 18 StrictMode (which double-mounts effects in dev) or any future
  // accidental remount, we hydrate at most ONCE per (user.id, mode)
  // tuple. Previously the user saw 2-4 redundant
  // /api/vendor-tax-registration/registration-progress calls during a
  // single new-signup page load, which contributed to the visible flicker.
  const hydratedForUserRef = useRef(null);
  useEffect(() => {
    const draft = loadSavedDraft();
    if (draft && !isReviewMode) return; // localStorage draft exists and not review mode, skip
    if (!user?.id) return;
    const hydrateKey = `${user.id}:${isReviewMode ? 'review' : 'edit'}`;
    if (hydratedForUserRef.current === hydrateKey) {
      return; // already hydrated for this user — no-op remount
    }
    hydratedForUserRef.current = hydrateKey;
    
    const hydrateFromBackend = async () => {
      try {
        const progress = await apiCall(`/api/vendor-tax-registration/registration-progress/${user.id}`);
        if (!progress) return;
        
        const backendStep = progress.current_step || 1;
        // In review mode, always start at step 1 so user can see/edit all their data
        if (!isReviewMode && backendStep > 1) {
          setCurrentStep(backendStep);
        }
        
        // iter-230j — Batch ALL hydration into a SINGLE setFormData call.
        // The previous implementation issued 4-7 back-to-back
        // setFormData(prev => ...) calls (tax_registration, bank_account,
        // pickup_address, supplier_details) which each scheduled a
        // re-render + re-fired the auto-save 1s timeout. Visually that
        // contributed to the "flash then flicker" loop. One merged setter
        // collapses it into a single render.
        const hydrationPatch = {};

        // Hydrate form from tax_registration data
        const taxReg = progress.tax_registration;
        if (taxReg) {
          const gstExempted = taxReg.gst_exempted || taxReg.tax_compliance_tier === 'tier_5_gst_exempt';
          const hasGstValue = gstExempted ? 'exempt' : (taxReg.has_gst ? 'yes' : 'no');
          
          Object.assign(hydrationPatch, {
            hasGST: hasGstValue,
            gstin: taxReg.gstin || '',
            gstVerified: taxReg.gst_verified || false,
            gstLegalName: taxReg.gst_legal_name || '',
            gstTradeName: taxReg.gst_trade_name || '',
            gstAdditionalTradeNames: taxReg.gst_additional_trade_names || [],
            gstBusinessAddress: taxReg.gst_business_address || '',
            gstParsedAddress: taxReg.gst_parsed_address || null,
            gstStatus: taxReg.gst_status || '',
            gstRegistrationDate: taxReg.gst_registration_date || '',
            gstConstitution: taxReg.gst_constitution || '',
            gstTaxpayerType: taxReg.gst_taxpayer_type || '',
            gstCoreBusinessActivity: taxReg.gst_core_business_activity || '',
            gstNatureOfBusiness: taxReg.gst_nature_of_business || [],
            gstStateJurisdiction: taxReg.gst_state_jurisdiction || '',
            gstCenterJurisdiction: taxReg.gst_center_jurisdiction || '',
            gstHsnCodes: taxReg.gst_hsn_codes || [],
            gstSacCodes: taxReg.gst_sac_codes || [],
            gstHasGoodsRegistration: taxReg.gst_has_goods_registration || false,
            gstHasServicesRegistration: taxReg.gst_has_services_registration || false,
            panNumber: taxReg.pan_number || '',
            panValid: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test((taxReg.pan_number || '').toUpperCase()),
            // iter-233-F — hydrate personal PAN + IEC from server.
            personalPan: taxReg.personal_pan || '',
            personalPanValid: (() => {
              const v = (taxReg.personal_pan || '').toUpperCase();
              return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v) && v[3] === 'P';
            })(),
            iecCode: taxReg.iec_code || '',
            businessName: taxReg.business_name || '',
            businessType: taxReg.business_type || 'individual',
            companyType: taxReg.company_type || '',
            annualTurnover: taxReg.annual_turnover || '',
            enrollmentId: taxReg.enrollment_id || '',
            enrollmentVerified: taxReg.enrollment_verified || false,
            applyEnrollmentLater: taxReg.apply_enrollment_later || false,
            gstExemptCategory: taxReg.gst_exempt_category || '',
            gstExemptReason: taxReg.gst_exempt_reason || '',
            gstExemptDeclared: taxReg.gst_exempted || false,
          });
        }
        
        // Hydrate bank details
        const bank = progress.bank_account;
        if (bank) {
          Object.assign(hydrationPatch, {
            accountNumber: bank.account_number || '',
            confirmAccountNumber: bank.account_number || '',
            ifscCode: bank.ifsc_code || '',
            bankName: bank.bank_name || '',
            branch: bank.branch || '',
            bankAddress: bank.bank_address || '',
            accountHolderName: bank.account_holder_name || '',
            bankVerified: bank.bank_verified || false,
          });
        }
        
        // Hydrate pickup address
        const primary = progress.pickup_address;
        if (primary) {
          Object.assign(hydrationPatch, {
            roomNumber: primary.room_number || '',
            streetLocality: primary.street_locality || '',
            landmark: primary.landmark || '',
            pincode: primary.pincode || '',
            city: primary.city || '',
            state: primary.state || '',
            useGSTAddress: primary.use_gst_address || false,
            // iter-229f: hydrate the delivery co-ordinator phone if
            // we've persisted one; otherwise leave blank and the
            // mobile/phone autofill in the input below will populate.
            deliveryCoordinatorPhone: primary.delivery_coordinator_phone || primary.phone || '',
          });
        }
        
        // Hydrate supplier details from progress response (more complete than user object)
        const supplier = progress.supplier_details || {};

        // Apply ALL hydration in ONE setter — collapses 4 re-renders into 1.
        setFormData(prev => ({
          ...prev,
          ...hydrationPatch,
          // Supplier section needs `prev` values to take precedence if already filled
          fullName: prev.fullName || supplier.full_name || user?.name || '',
          mobileNumber: prev.mobileNumber || supplier.mobile_number || user?.mobile || '',
          whatsappNumber: prev.whatsappNumber || (supplier.whatsapp_id ? supplier.whatsapp_id.replace(/^91/, '') : ''),
          whatsappOptIn: prev.whatsappOptIn || supplier.giri_active || false,
          emailId: prev.emailId || supplier.email || user?.email || '',
          agreeToTerms: isReviewMode ? true : prev.agreeToTerms, // Already agreed if completed
        }));
        
        // Hydrate store identity from progress response (for review mode)
        if (isReviewMode) {
          const identity = progress.identity || {};
          const agreements = progress.agreements || {};
          if (identity.store_name) {
            setStoreIdentity(prev => ({
              ...prev,
              storeName: identity.store_name || '',
              legalName: identity.legal_name || '',
              namingPolicyAccepted: !!agreements.naming_policy_accepted,
            }));
          }
        }
        
        setHasDraft(true);
      } catch (e) {
        console.error('Error hydrating from backend:', e);
      }
    };
    
    hydrateFromBackend();
  }, [user?.id]); // Run when user ID is available


  // Auto-save effect - saves form data to localStorage on every change
  useEffect(() => {
    // Don't save if form is completely untouched
    const hasAnyData = formData.gstin || formData.panNumber || formData.businessName || 
      formData.accountNumber || formData.fullName || formData.pincode ||
      formData.enrollmentId || formData.roomNumber || currentStep > 1;
    if (!hasAnyData) {
      return;
    }
    
    const saveTimeout = setTimeout(() => {
      try {
        localStorage.setItem(AUTO_SAVE_KEY, JSON.stringify({
          data: formData,
          savedAt: Date.now(),
          step: currentStep
        }));
      } catch (e) {
        console.error('Error auto-saving draft:', e);
      }
    }, 1000); // Debounce 1 second
    
    return () => clearTimeout(saveTimeout);
  }, [formData, currentStep, AUTO_SAVE_KEY]);
  
  // Clear draft after successful completion
  const clearDraft = () => {
    localStorage.removeItem(AUTO_SAVE_KEY);
    setHasDraft(false);
  };

  // Dynamic steps based on onboarding profile
  const baseSteps = [
    { id: 1, title: 'Business Details', icon: Building },
    { id: 2, title: isPickupAddressStep ? 'Pickup Address' : 'Business Address', icon: MapPin, skip: shouldSkipPickupAddress },
    { id: 3, title: 'Bank Details', icon: CreditCard },
    { id: 4, title: 'Supplier Details', icon: User },
    { id: 5, title: 'Store Identity', icon: Store },
    { id: 6, title: 'Choose Plan', icon: Crown }
  ];
  
  // Filter out skipped steps for display
  const steps = baseSteps.filter(step => !step.skip);
  
  // Map current step to actual step ID (accounting for skipped steps)
  const getActualStepId = (displayStep) => {
    let actualStep = displayStep;
    for (let i = 0; i < displayStep; i++) {
      if (baseSteps[i]?.skip) actualStep++;
    }
    return actualStep;
  };
  
  const getDisplayStep = (actualStep) => {
    let displayStep = actualStep;
    for (let i = 0; i < actualStep; i++) {
      if (baseSteps[i]?.skip) displayStep--;
    }
    return displayStep;
  };
  
  // State for store identity (Step 5 now)
  const [storeIdentity, setStoreIdentity] = useState({
    legalName: '',
    storeName: '',
    namingPolicyAccepted: false,
    verificationChoice: 'skip', // 'trademark', 'legacy_store', 'skip'
    // Trademark fields
    tmCertificateUrl: '',
    tmNumber: '',
    tmStatus: '',
    brandName: '',
    // Legacy store fields
    shopLicenseUrl: '',
    storeEstablishedYear: '',
    storeAddress: ''
  });
  const [storeNameValidation, setStoreNameValidation] = useState({
    checking: false,
    isValid: null,
    message: '',
    suggestions: []
  });
  
  // State for subscription plans
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('annual');
  const [loadingPlans, setLoadingPlans] = useState(false);
  
  // Fetch subscription plans when reaching step 6
  useEffect(() => {
    if (currentStep === 6) {
      fetchSubscriptionPlans();
    }
  }, [currentStep]);
  
  const fetchSubscriptionPlans = async () => {
    try {
      setLoadingPlans(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscription-plans/public`);
      const data = await response.json();
      setSubscriptionPlans(data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  // Validate store name in real-time
  const validateStoreName = useCallback(async (name) => {
    if (!name || name.length < 3) {
      setStoreNameValidation({
        checking: false,
        isValid: null,
        message: name ? 'Store name must be at least 3 characters' : '',
        suggestions: []
      });
      return;
    }

    setStoreNameValidation(prev => ({ ...prev, checking: true }));

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/brand-registry/validate-store-name?store_name=${encodeURIComponent(name)}`,
        { method: 'POST' }
      );
      const data = await response.json();
      
      setStoreNameValidation({
        checking: false,
        isValid: data.is_valid,
        message: data.message,
        suggestions: data.suggestions || []
      });
    } catch (error) {
      setStoreNameValidation({
        checking: false,
        isValid: null,
        message: 'Failed to validate store name',
        suggestions: []
      });
    }
  }, []);

  // Debounced store name validation
  useEffect(() => {
    const timer = setTimeout(() => {
      if (storeIdentity.storeName) {
        validateStoreName(storeIdentity.storeName);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [storeIdentity.storeName, validateStoreName]);

  // Handle store identity changes
  const handleStoreIdentityChange = (field, value) => {
    setStoreIdentity(prev => ({ ...prev, [field]: value }));
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Clear validation error when user types
      if (validationErrors[field]) {
        setValidationErrors(prev => ({ ...prev, [field]: null }));
      }
      
      // Auto-fill pickup address when GST address checkbox is checked
      if (field === 'useGSTAddress' && value && prev.gstBusinessAddress) {
        const address = prev.gstBusinessAddress;
        const pincodeMatch = address.match(/(\d{6})\b/);
        updated.pincode = pincodeMatch ? pincodeMatch[1] : '';
        
        const parts = address.split(',').map(p => p.trim());
        updated.roomNumber = parts[0] || '';
        updated.streetLocality = parts[1] || '';
        updated.landmark = parts[2] || '';
      }
      
      // Auto-fill PAN from GST number (characters 3-12 of GSTIN are the PAN)
      if (field === 'gstin' && value && value.length >= 12) {
        const extractedPan = value.substring(2, 12).toUpperCase();
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (panRegex.test(extractedPan)) {
          updated.panNumber = extractedPan;
          updated.panValid = true;
        }
      }
      
      // PAN validation
      if (field === 'panNumber') {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        updated.panValid = panRegex.test(value.trim().toUpperCase());
      }
      
      return updated;
    });
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 1: // Business Details (was step 2)
        if (!formData.panNumber) {
          errors.panNumber = 'PAN number is required';
        } else if (!formData.panValid) {
          errors.panNumber = 'Invalid PAN format. Use format: ABCDE1234F';
        }
        
        // Business name is required only if no GST (otherwise GST legal name is used)
        if (formData.hasGST !== 'yes' && !formData.businessName) {
          errors.businessName = 'Business name is required';
        }
        
        if (formData.hasGST === 'yes') {
          if (!formData.gstin) {
            errors.gstin = 'GSTIN is required when you have GST';
          } else if (!formData.gstVerified) {
            errors.gstin = 'Please verify your GSTIN before proceeding';
          }
          // iter-233-F — Personal PAN required for GST users (T3/T4).
          // Razorpay Stakeholder rejects company PANs; the director /
          // proprietor's individual PAN (4th char=P) is needed.
          if (!formData.personalPan) {
            errors.personalPan = 'Personal PAN is required for GST-registered businesses (director/proprietor PAN).';
          } else {
            const pp = formData.personalPan.toUpperCase();
            if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(pp)) {
              errors.personalPan = 'Invalid PAN format. Use format: ABCDE1234F';
            } else if (pp[3] !== 'P') {
              errors.personalPan = "4th character must be 'P' (Individual). Use the proprietor's / director's personal PAN here.";
            }
          }
          // iter-233-F — IEC is optional, but if entered must be 10-14 chars.
          if (formData.iecCode) {
            const iec = formData.iecCode.toUpperCase().trim();
            if (iec.length < 10 || iec.length > 14) {
              errors.iecCode = 'IEC is typically a 10-character DGFT-issued code.';
            }
          }
        } else if (formData.hasGST === 'exempt') {
          if (!formData.gstExemptCategory) {
            errors.gstExemptCategory = 'Please select your exempt supply category';
          }
          if (!formData.gstExemptDeclared) {
            errors.gstExemptDeclared = 'You must confirm that your supplies are GST-exempt';
          }
        } else {
          // No GST - check if enrollment ID or "apply later" is selected
          if (!formData.applyEnrollmentLater && !formData.enrollmentId) {
            errors.enrollmentId = 'Enter Enrollment ID or select "I will apply later"';
          }
        }
        break;
        
      case 2: // Pickup Address (was step 3)
        if (!formData.pincode) {
          errors.pincode = 'Pincode is required';
        } else if (formData.pincode.length !== 6) {
          errors.pincode = 'Pincode must be 6 digits';
        }
        if (!formData.city) {
          errors.city = 'City is required';
        }
        if (!formData.state) {
          errors.state = 'State is required';
        }
        break;
        
      case 3: // Bank Details (was step 4)
        if (!formData.accountHolderName) {
          errors.accountHolderName = 'Account holder name is required';
        }
        if (!formData.accountNumber) {
          errors.accountNumber = 'Account number is required';
        }
        if (!formData.confirmAccountNumber) {
          errors.confirmAccountNumber = 'Please confirm account number';
        } else if (formData.accountNumber !== formData.confirmAccountNumber) {
          errors.confirmAccountNumber = 'Account numbers do not match';
        }
        if (!formData.ifscCode) {
          errors.ifscCode = 'IFSC code is required';
        } else if (!formData.bankVerified) {
          errors.ifscCode = 'Please verify your bank details before proceeding';
        }
        break;
        
      case 4: // Supplier Details (was step 5)
        if (!formData.fullName) {
          errors.fullName = 'Full name is required';
        }
        if (!formData.mobileNumber) {
          errors.mobileNumber = 'Mobile number is required';
        } else if (!/^[6-9]\d{9}$/.test(formData.mobileNumber)) {
          errors.mobileNumber = 'Enter a valid 10-digit mobile number';
        }
        if (formData.whatsappNumber && !/^[6-9]\d{9}$/.test(formData.whatsappNumber)) {
          errors.whatsappNumber = 'Enter a valid 10-digit WhatsApp number';
        }
        if (!formData.agreeToTerms) {
          errors.agreeToTerms = 'You must read and agree to the Seller Agreement and Privacy Policy';
        }
        break;
        
      case 5: // Store Identity (was step 1)
        if (!storeIdentity.storeName || storeIdentity.storeName.length < 3) {
          errors.storeName = 'Store display name is required (min 3 characters)';
        } else if (storeNameValidation.isValid === false) {
          errors.storeName = storeNameValidation.message;
        }
        
        if (!storeIdentity.namingPolicyAccepted) {
          errors.namingPolicyAccepted = 'You must accept the naming policy to proceed';
        }
        break;
        
      case 6: // Choose Plan - no validation needed
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  // Fetch CAPTCHA for GST verification
  const fetchGSTCaptcha = async () => {
    setCaptchaData(prev => ({ ...prev, loading: true }));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/captcha`);
      const data = await response.json();
      
      if (response.ok) {
        setCaptchaData({
          sessionId: data.session_id,
          captchaImage: data.captcha_image,
          captchaInput: '',
          loading: false,
          showCaptcha: true
        });
        toast({ 
          title: 'CAPTCHA Loaded', 
          description: 'Please solve the CAPTCHA to verify your GST.' 
        });
      } else {
        throw new Error(data.detail || 'Failed to fetch CAPTCHA');
      }
    } catch (error) {
      setCaptchaData(prev => ({ ...prev, loading: false }));
      toast({ 
        title: '\u274c Error', 
        description: error.message || 'Failed to load CAPTCHA. Please try again.', 
        variant: 'destructive' 
      });
    }
  };

  // Verify GSTIN with CAPTCHA
  const verifyGSTIN = async () => {
    if (formData.gstin.length !== 15) {
      toast({ 
        title: 'Invalid GSTIN', 
        description: 'GSTIN must be exactly 15 characters. Please check and try again.', 
        variant: 'destructive' 
      });
      return;
    }

    // If CAPTCHA not shown yet, fetch it first
    if (!captchaData.showCaptcha) {
      await fetchGSTCaptcha();
      return;
    }

    // Validate CAPTCHA input
    if (!captchaData.captchaInput || captchaData.captchaInput.trim().length === 0) {
      toast({ 
        title: 'CAPTCHA Required', 
        description: 'Please enter the CAPTCHA text to verify your GST.', 
        variant: 'destructive' 
      });
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_id: captchaData.sessionId,
          gstin: formData.gstin,
          captcha: captchaData.captchaInput
        })
      });

      // iter-230ac — Tolerate non-JSON error bodies (e.g. K8s ingress
      // HTML pages on 502/504). Try JSON first; if that throws, fall
      // back to text so the user still sees something useful.
      let data;
      try {
        data = await response.json();
      } catch {
        const text = await response.text().catch(() => '');
        data = { detail: text || `HTTP ${response.status} ${response.statusText}` };
      }

      // FastAPI returns errors as `{detail: "..."}`. Older custom
      // handlers may use `{error: "..."}`. Read both and prefer the
      // one that's present.
      const serverMsg = data.detail || data.error;

      if (!response.ok) {
        // Captcha/session errors come back as 400. Reset the captcha
        // panel so the user gets a fresh image automatically.
        const lower = String(serverMsg || '').toLowerCase();
        const isSessionIssue = lower.includes('captcha')
          || lower.includes('session')
          || lower.includes('expired');
        if (isSessionIssue) {
          setCaptchaData({
            sessionId: null,
            captchaImage: null,
            captchaInput: '',
            loading: false,
            showCaptcha: false,
          });
        }
        toast({
          title: '\u274c Verification Failed',
          description: serverMsg
            || `Verification failed (HTTP ${response.status}). Please refresh the CAPTCHA and try again.`,
          variant: 'destructive',
        });
        return;
      }

      if (data.success) {
        // Use parsed_address for cleaner pre-fill, fallback to manual parsing
        let businessAddress = '';
        let parsedAddress = data.parsed_address || null;
        
        if (parsedAddress && parsedAddress.full_address) {
          businessAddress = parsedAddress.full_address;
        } else if (data.principal_address) {
          const addr = data.principal_address.addr || data.principal_address;
          const parts = [
            addr.bno, addr.flno, addr.bnm, addr.st, 
            addr.loc, addr.dst, addr.stcd, addr.pncd
          ].filter(Boolean);
          businessAddress = parts.join(', ');
        }

        // Extract HSN and SAC codes for product/service validation
        const hsnCodes = data.hsn_codes || [];
        const sacCodes = data.sac_codes || [];

        setFormData(prev => ({
          ...prev,
          gstVerified: true,
          gstLegalName: data.legal_name || '',
          gstTradeName: data.trade_name || '',
          gstAdditionalTradeNames: data.additional_trade_names || [],
          gstBusinessAddress: businessAddress || '',
          gstParsedAddress: parsedAddress,  // Structured address for pickup/return pre-fill
          gstStatus: data.status || '',
          gstRegistrationDate: data.registration_date || data.effective_date || '',
          gstConstitution: data.constitution_of_business || '',
          gstTaxpayerType: data.taxpayer_type || '',
          gstCoreBusinessActivity: data.core_business_activity || '',
          gstNatureOfBusiness: data.nature_of_business || [],
          gstStateJurisdiction: data.state_jurisdiction || '',
          gstCenterJurisdiction: data.center_jurisdiction || '',
          gstStateJurisdictionDetails: data.state_jurisdiction_details || null,
          gstCenterJurisdictionDetails: data.center_jurisdiction_details || null,
          gstEinvoiceStatus: data.einvoice_status || '',
          gstEinvoiceEnabled: data.einvoice_enabled || false,
          gstAadhaarValidated: data.aadhaar_validated || '',
          gstAadhaarValidationDate: data.aadhaar_validation_date || '',
          gstEkycVerified: data.ekyc_verified || '',
          gstFieldVisitConducted: data.field_visit_conducted || '',
          gstPrincipalAddress: data.principal_address || null,
          // HSN/SAC codes for product validation
          gstHsnCodes: hsnCodes,
          gstSacCodes: sacCodes,
          gstHasGoodsRegistration: data.has_goods_registration || hsnCodes.length > 0,
          gstHasServicesRegistration: data.has_services_registration || sacCodes.length > 0,
          // Pre-fill business name from legal/trade name
          businessName: data.trade_name || data.legal_name || prev.businessName
        }));
        
        // Reset CAPTCHA state
        setCaptchaData({
          sessionId: null,
          captchaImage: null,
          captchaInput: '',
          loading: false,
          showCaptcha: false
        });

        // Show enhanced success message with HSN/SAC info
        let successMsg = 'Your GST details have been verified from the Government Portal.';
        if (hsnCodes.length > 0 || sacCodes.length > 0) {
          successMsg += ` Found ${hsnCodes.length} HSN codes (goods) and ${sacCodes.length} SAC codes (services).`;
        }

        toast({ 
          title: '\u2705 GST Verified Successfully', 
          description: successMsg
        });
      } else {
        // Check if it's a CAPTCHA error - need to refresh
        if (data.error?.includes('CAPTCHA') || data.error?.includes('session') || data.error?.includes('expired')) {
          setCaptchaData(prev => ({ ...prev, captchaInput: '', showCaptcha: false }));
          toast({ 
            title: '\u274c Verification Failed', 
            description: 'Session expired or invalid CAPTCHA. Please try again.', 
            variant: 'destructive' 
          });
        } else {
          toast({ 
            title: '\u274c Verification Failed', 
            description: data.error || 'Invalid GSTIN or CAPTCHA. Please verify and try again.', 
            variant: 'destructive' 
          });
        }
      }
    } catch (error) {
      // iter-230ac — Only show the "check your internet" copy for an
      // actual network failure (fetch throws a TypeError when the
      // network request itself can't be made). For everything else,
      // surface the error message so the user/support can diagnose.
      const isNetworkErr =
        error instanceof TypeError &&
        /failed to fetch|network|load failed/i.test(String(error.message || ''));
      toast({
        title: '\u274c Error',
        description: isNetworkErr
          ? 'Network error reaching the server. Please check your internet connection and try again.'
          : (error?.message || 'Failed to verify GST. Please refresh the CAPTCHA and try again.'),
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  // Fetch CAPTCHA for Enrollment ID verification
  const fetchEnrollmentCaptcha = async () => {
    setEnrollmentCaptchaData(prev => ({ ...prev, loading: true }));
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/captcha`);
      const data = await response.json();
      
      if (response.ok) {
        setEnrollmentCaptchaData({
          sessionId: data.session_id,
          captchaImage: data.captcha_image,
          captchaInput: '',
          loading: false,
          showCaptcha: true
        });
        toast({ 
          title: 'CAPTCHA Loaded', 
          description: 'Please solve the CAPTCHA to verify your Enrollment ID.' 
        });
      } else {
        throw new Error(data.detail || 'Failed to fetch CAPTCHA');
      }
    } catch (error) {
      setEnrollmentCaptchaData(prev => ({ ...prev, loading: false }));
      toast({ 
        title: '\u274c Error', 
        description: error.message || 'Failed to load CAPTCHA. Please try again.', 
        variant: 'destructive' 
      });
    }
  };

  // Verify Enrollment ID with CAPTCHA
  const verifyEnrollmentId = async () => {
    if (!formData.enrollmentId || formData.enrollmentId.length < 10) {
      toast({ 
        title: 'Invalid Enrollment ID', 
        description: 'Please enter a valid Enrollment ID (e.g., AA0408210000001).', 
        variant: 'destructive' 
      });
      return;
    }

    // If CAPTCHA not shown yet, fetch it first
    if (!enrollmentCaptchaData.showCaptcha) {
      await fetchEnrollmentCaptcha();
      return;
    }

    // Validate CAPTCHA input
    if (!enrollmentCaptchaData.captchaInput || enrollmentCaptchaData.captchaInput.trim().length === 0) {
      toast({ 
        title: 'CAPTCHA Required', 
        description: 'Please enter the CAPTCHA text to verify your Enrollment ID.', 
        variant: 'destructive' 
      });
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/gst-verification/verify-enrollment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          session_id: enrollmentCaptchaData.sessionId,
          enrollment_id: formData.enrollmentId,
          captcha: enrollmentCaptchaData.captchaInput
        })
      });

      const data = await response.json();

      if (data.success) {
        setFormData(prev => ({
          ...prev,
          enrollmentVerified: true,
          businessName: data.trade_name || data.applicant_name || prev.businessName
        }));
        
        setEnrollmentVerificationResult({
          success: true,
          status: data.status,
          applicantName: data.applicant_name,
          tradeName: data.trade_name,
          applicationDate: data.application_date,
          state: data.state
        });
        
        // Reset CAPTCHA state
        setEnrollmentCaptchaData({
          sessionId: null,
          captchaImage: null,
          captchaInput: '',
          loading: false,
          showCaptcha: false
        });

        toast({ 
          title: '\u2705 Enrollment ID Verified Successfully', 
          description: `Status: ${data.status || 'Verified'}` 
        });
      } else {
        // Check if it's a CAPTCHA error - need to refresh
        if (data.error?.includes('CAPTCHA') || data.error?.includes('session') || data.error?.includes('expired')) {
          setEnrollmentCaptchaData(prev => ({ ...prev, captchaInput: '', showCaptcha: false }));
          toast({ 
            title: '\u274c Verification Failed', 
            description: 'Session expired or invalid CAPTCHA. Please try again.', 
            variant: 'destructive' 
          });
        } else {
          setEnrollmentVerificationResult({
            success: false,
            error: data.error
          });
          toast({ 
            title: '\u274c Verification Failed', 
            description: data.error || 'Invalid Enrollment ID or CAPTCHA. Please verify and try again.', 
            variant: 'destructive' 
          });
        }
      }
    } catch (error) {
      // iter-230ac — Surface real errors; reserve the "check internet"
      // copy for actual network failures only.
      const isNetworkErr =
        error instanceof TypeError &&
        /failed to fetch|network|load failed/i.test(String(error.message || ''));
      toast({
        title: '\u274c Error',
        description: isNetworkErr
          ? 'Network error reaching the server. Please check your internet connection and try again.'
          : (error?.message || 'Failed to verify Enrollment ID. Please refresh the CAPTCHA and try again.'),
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  const verifyIFSC = async () => {
    const errors = {};
    
    if (formData.ifscCode.length !== 11) {
      errors.ifscCode = 'IFSC code must be exactly 11 characters';
    }
    
    if (formData.accountNumber !== formData.confirmAccountNumber) {
      errors.confirmAccountNumber = 'Account numbers do not match';
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast({ 
        title: '\u274c Validation Error', 
        description: 'Please fix the errors before verifying bank details.', 
        variant: 'destructive' 
      });
      return;
    }

    setVerifying(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vendor-tax-registration/verify-ifsc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ifsc_code: formData.ifscCode })
      });

      const data = await response.json();

      if (data.success && data.verified) {
        setFormData(prev => ({
          ...prev,
          bankVerified: true,
          bankName: data.bank_name || '',
          branch: data.branch || '',
          bankAddress: data.bank_address || ''
        }));
        toast({ 
          title: '\u2705 Bank Verified Successfully', 
          description: `${data.bank_name} - ${data.branch}` 
        });
      } else {
        toast({ 
          title: '\u274c Verification Failed', 
          description: data.message || 'Invalid IFSC code. Please verify and try again.', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      // iter-230ac — Surface real errors; reserve the "check internet"
      // copy for actual network failures only.
      const isNetworkErr =
        error instanceof TypeError &&
        /failed to fetch|network|load failed/i.test(String(error.message || ''));
      toast({
        title: '\u274c Error',
        description: isNetworkErr
          ? 'Network error reaching the server. Please check your internet connection and try again.'
          : (error?.message || 'Failed to verify IFSC. Please try again.'),
        variant: 'destructive',
      });
    } finally {
      setVerifying(false);
    }
  };

  const canProceed = () => {
    const errors = validateStep(currentStep);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    // Validate current step
    const errors = validateStep(currentStep);
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      
      // Show first error in toast
      const firstError = Object.values(errors)[0];
      toast({
        title: '\u274c Please fix the following errors',
        description: firstError,
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      let stepData = {};
      let stepName = '';

      switch (currentStep) {
        case 1: // Business Details (NEW Step 1)
          stepName = 'business_details';
          stepData = {
            has_gst: formData.hasGST === 'yes',
            gstin: formData.gstin,
            gst_type: formData.hasGST === 'yes' ? 'regular' : 'none',
            gst_verified: formData.gstVerified,
            gst_legal_name: formData.gstLegalName,
            gst_trade_name: formData.gstTradeName,
            gst_additional_trade_names: formData.gstAdditionalTradeNames || [],
            gst_business_address: formData.gstBusinessAddress,
            gst_parsed_address: formData.gstParsedAddress,  // Structured for pickup pre-fill
            gst_status: formData.gstStatus,
            gst_registration_date: formData.gstRegistrationDate,
            gst_constitution: formData.gstConstitution,
            gst_taxpayer_type: formData.gstTaxpayerType,
            gst_core_business_activity: formData.gstCoreBusinessActivity,
            gst_nature_of_business: formData.gstNatureOfBusiness,
            gst_state_jurisdiction: formData.gstStateJurisdiction,
            gst_center_jurisdiction: formData.gstCenterJurisdiction,
            gst_state_jurisdiction_details: formData.gstStateJurisdictionDetails,
            gst_center_jurisdiction_details: formData.gstCenterJurisdictionDetails,
            gst_einvoice_status: formData.gstEinvoiceStatus,
            gst_einvoice_enabled: formData.gstEinvoiceEnabled,
            gst_aadhaar_validated: formData.gstAadhaarValidated,
            gst_aadhaar_validation_date: formData.gstAadhaarValidationDate,
            gst_ekyc_verified: formData.gstEkycVerified,
            gst_field_visit_conducted: formData.gstFieldVisitConducted,
            gst_principal_address: formData.gstPrincipalAddress,
            // HSN/SAC codes for product/service validation
            gst_hsn_codes: formData.gstHsnCodes || [],
            gst_sac_codes: formData.gstSacCodes || [],
            gst_has_goods_registration: formData.gstHasGoodsRegistration,
            gst_has_services_registration: formData.gstHasServicesRegistration,
            pan_number: formData.panNumber,
            // iter-233-F — Personal PAN (required for GST users / T3/T4)
            // and IEC code (optional, auto-promotes T3 → T4).
            personal_pan: formData.personalPan || undefined,
            iec_code: formData.iecCode || undefined,
            business_name: formData.hasGST === 'yes' ? (formData.gstTradeName || formData.gstLegalName || formData.businessName) : formData.businessName,
            business_type: formData.businessType,
            // Canonical SSOT (see backend/utils/vendor_enums.py) — kept consistent
            // with admin VendorDetailsNew + vendor self-serve Profile.jsx so values
            // round-trip cleanly.
            company_type: formData.companyType,
            annual_turnover: formData.annualTurnover,
            enrollment_id: formData.enrollmentId,
            enrollment_verified: formData.enrollmentVerified,
            apply_enrollment_later: formData.applyEnrollmentLater,
            tax_compliance_tier: formData.hasGST === 'yes'
              ? 'tier_3_full_gst'
              : formData.hasGST === 'exempt'
                ? 'tier_5_gst_exempt'
                : 'tier_1_pan_only',
            gst_exempted: formData.hasGST === 'exempt',
            gst_exempt_category: formData.gstExemptCategory || null,
            gst_exempt_reason: formData.gstExemptReason || null
          };
          break;
          
        case 2: // Pickup Address (NEW Step 2)
          stepName = 'pickup_address';
          stepData = {
            use_gst_address: formData.useGSTAddress,
            room_number: formData.roomNumber,
            street_locality: formData.streetLocality,
            landmark: formData.landmark,
            pincode: formData.pincode,
            city: formData.city,
            state: formData.state,
            // iter-229f — surface the rider's contact number so the
            // shipping/pickup flow never hits a "missing phone" path.
            delivery_coordinator_phone: formData.deliveryCoordinatorPhone || formData.mobileNumber || '',
            phone: formData.deliveryCoordinatorPhone || formData.mobileNumber || '',
          };
          break;
          
        case 3: // Bank Details (NEW Step 3)
          stepName = 'bank_details';
          stepData = {
            account_number: formData.accountNumber,
            account_holder_name: formData.accountHolderName,
            ifsc_code: formData.ifscCode,
            bank_name: formData.bankName,
            branch: formData.branch,
            bank_address: formData.bankAddress,
            bank_verified: formData.bankVerified
          };
          break;
          
        case 4: // Supplier Details (NEW Step 4)
          stepName = 'supplier_details';
          stepData = {
            full_name: formData.fullName,
            mobile_number: formData.mobileNumber,
            email: formData.emailId,
            whatsapp_number: formData.whatsappNumber,
            whatsapp_opt_in: formData.whatsappOptIn,
            terms_accepted: formData.agreeToTerms,
            whatsapp_communications_opt_in: formData.whatsappCommunicationsOptIn
          };
          break;
          
        case 5: // Store Identity (NEW Step 5)
          stepName = 'store_identity';
          // Use business name from GST if available, otherwise from form
          const legalName = formData.hasGST === 'yes' ? (formData.gstLegalName || formData.businessName) : formData.businessName;
          
          // Call setup-store-identity API
          await apiCall('/api/brand-registry/setup-store-identity', {
            method: 'POST',
            body: JSON.stringify({
              legal_name: legalName,
              store_name: storeIdentity.storeName,
              naming_policy_accepted: storeIdentity.namingPolicyAccepted,
              ip_address: null
            })
          });
          
          // If verification choice is not skip, submit verification request
          if (storeIdentity.verificationChoice !== 'skip') {
            try {
              await apiCall('/api/brand-registry/onboarding/verification-choice', {
                method: 'POST',
                body: JSON.stringify({
                  choice: storeIdentity.verificationChoice,
                  tm_certificate_url: storeIdentity.tmCertificateUrl || null,
                  tm_number: storeIdentity.tmNumber || null,
                  tm_status: storeIdentity.tmStatus || null,
                  brand_name: storeIdentity.brandName || null,
                  shop_license_url: storeIdentity.shopLicenseUrl || null,
                  store_established_year: storeIdentity.storeEstablishedYear ? parseInt(storeIdentity.storeEstablishedYear) : null,
                  store_address: storeIdentity.storeAddress || null
                })
              });
              
              toast({
                title: '✅ Verification Request Submitted',
                description: 'Your verification request will be reviewed by our team.',
              });
            } catch (verifyError) {
              console.error('Verification request error:', verifyError);
            }
          }
          
          // Continue to Choose Plan step
          setCurrentStep(6);
          setLoading(false);
          return;
          
        case 6: // Choose Plan (NEW Step 6)
          stepName = 'subscription';
          stepData = {
            subscription_selected: !!selectedPlan,
            plan_id: selectedPlan?.id || null,
            skipped: false
          };
          break;
          
        default:
          break;
      }

      const response = await apiCall('/api/vendor-tax-registration/registration-step', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: user.id,
          step: stepName,
          data: stepData
        })
      });

      if (response.success) {
        // In upgrade mode, redirect back to profile after step 1 (GST verification)
        if (isUpgradeMode && currentStep === 1) {
          toast({ 
            title: '✅ GST Verified Successfully', 
            description: 'Your KYC level has been upgraded to Pan-India Sales!' 
          });
          navigate('/vendor/profile?tab=business');
          return;
        }
        
        if (currentStep < 6) {
          setCurrentStep(currentStep + 1);
          setValidationErrors({}); // Clear errors when moving to next step
        } else {
          // Step 6 completed - now subscribe if plan selected
          if (selectedPlan) {
            try {
              await apiCall('/api/vendor-subscription/subscribe', {
                method: 'POST',
                body: JSON.stringify({
                  plan_id: selectedPlan.id,
                  billing_cycle: billingCycle,
                  apply_trial: true
                })
              });
              toast({ 
                title: '🎉 Welcome!', 
                description: `Your 30-day trial of ${selectedPlan.plan_name} has started!`
              });
            } catch (subError) {
              console.error('Subscription error:', subError);
              toast({ 
                title: 'Plan Selection Saved', 
                description: 'You can complete subscription from your dashboard.'
              });
            }
          }
          toast({ 
            title: '\u2705 Registration Complete', 
            description: 'Your vendor registration is complete! You can now start listing products.' 
          });
          clearDraft(); // Clear auto-saved draft on successful completion
          // Update user context so PrivateRoute stops redirecting to onboarding
          updateUser({ onboarding_completed: true, registration_step: 5 });
          // iter-230j — Invalidate the artifact-driven verdict cache so
          // PrivateRoute re-fetches and observes `completed=true` from
          // the live DB row on the very next navigation. Without this
          // the stale "incomplete" cache would keep redirecting back
          // to /vendor/tax-registration even after a clean finish.
          clearOnboardingArtifactsCache(user?.id);
          localStorage.removeItem('onboarding_redirect');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      toast({ 
        title: '\u274c Error', 
        description: error.message || 'Failed to save. Please try again.', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({}); // Clear errors when going back
    }
  };

  // Handle skip subscription
  const handleSkipSubscription = async () => {
    setLoading(true);
    try {
      // iter-221c — Skip auto-enrols vendor in Professional plan with 30-day
      // trial (no more "planless" state). Backend logs the source as
      // 'vendor_skip' for audit. Idempotent: re-running on a vendor with an
      // active sub is a no-op.
      let trialResult = null;
      try {
        trialResult = await apiCall('/api/vendor-subscription/skip-and-start-trial', {
          method: 'POST'
        });
      } catch (trialErr) {
        // Don't block onboarding completion if trial activation fails;
        // the daily trial-gate sweep will retry. Log for visibility.
        console.warn('[SkipPlan] trial auto-enrol failed:', trialErr);
      }

      await apiCall('/api/vendor-tax-registration/registration-step', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: user.id,
          step: 'subscription',
          data: {
            subscription_selected: false,
            plan_id: trialResult?.subscription_id || null,
            skipped: true,
            auto_enrolled_trial: !!trialResult?.success,
            trial_end: trialResult?.trial_end || null
          }
        })
      });

      const trialDays = trialResult?.trial_days_remaining ?? 30;
      toast({
        title: trialResult?.success ? `30-day Professional trial started` : 'Registration Complete',
        description: trialResult?.success
          ? `You're on the Professional plan free for ${trialDays} days. Pick any plan before then to continue without interruption.`
          : 'You can select a subscription plan anytime from your dashboard.'
      });
      clearDraft(); // Clear auto-saved draft on successful completion
      // Update user context so PrivateRoute stops redirecting to onboarding
      updateUser({ onboarding_completed: true, registration_step: 5 });
      // iter-230j — invalidate artifact verdict cache (see note above)
      clearOnboardingArtifactsCache(user?.id);
      localStorage.removeItem('onboarding_redirect');
      navigate('/dashboard');
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.message || 'Failed to skip. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1: // Business Details (NEW Step 1)
        return <BusinessDetailsStep 
          formData={formData} 
          onChange={handleInputChange}
          onVerifyGST={verifyGSTIN}
          onRefreshCaptcha={fetchGSTCaptcha}
          captchaData={captchaData}
          setCaptchaData={setCaptchaData}
          onVerifyEnrollment={verifyEnrollmentId}
          onRefreshEnrollmentCaptcha={fetchEnrollmentCaptcha}
          enrollmentCaptchaData={enrollmentCaptchaData}
          setEnrollmentCaptchaData={setEnrollmentCaptchaData}
          enrollmentVerificationResult={enrollmentVerificationResult}
          verifying={verifying}
          errors={validationErrors}
          onboardingProfile={onboardingProfile}
        />;
      case 2: // Pickup Address (NEW Step 2) - may be skipped for service-only vendors
        if (shouldSkipPickupAddress) {
          // Auto-advance to next step
          setCurrentStep(3);
          return null;
        }
        return <PickupAddressStep
          formData={formData}
          onChange={handleInputChange}
          errors={validationErrors}
          isPickupAddress={isPickupAddressStep}
        />;
      case 3: // Bank Details (NEW Step 3)
        return <BankDetailsStep 
          formData={formData} 
          onChange={handleInputChange}
          onVerifyIFSC={verifyIFSC}
          verifying={verifying}
          errors={validationErrors}
        />;
      case 4: // Supplier Details (NEW Step 4)
        return <SupplierDetailsStep 
          formData={formData} 
          onChange={handleInputChange}
          errors={validationErrors}
          user={user}
        />;
      case 5: // Store Identity (NEW Step 5)
        return <StoreIdentityStep 
          storeIdentity={storeIdentity}
          onChange={handleStoreIdentityChange}
          validation={storeNameValidation}
          errors={validationErrors}
          formData={formData}
        />;
      case 6: // Choose Plan (NEW Step 6)
        return <SubscriptionSelectionStep 
          plans={subscriptionPlans}
          selectedPlan={selectedPlan}
          onSelectPlan={setSelectedPlan}
          billingCycle={billingCycle}
          onBillingCycleChange={setBillingCycle}
          loading={loadingPlans}
        />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* iter-230k — Sign Out is reachable from every onboarding page. */}
      <OnboardingHeader subtitle="Vendor Onboarding" />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Upgrade Mode Banner */}
        {isUpgradeMode && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-6 w-6 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800">GST Verification Upgrade</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Complete GST verification to upgrade your KYC level from PAN Only to Pan-India Sales.
                  After verification, your business address will be auto-populated from GST records.
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={() => navigate('/vendor/profile?tab=business')}
                >
                  ← Back to Profile
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Draft Restoration Banner */}
        {hasDraft && !isUpgradeMode && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Save className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-800">Draft Restored</p>
                  <p className="text-xs text-green-600">Your previous progress has been restored. Continue where you left off.</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                className="text-green-700 hover:text-green-800 hover:bg-green-100"
                onClick={() => {
                  clearDraft();
                  window.location.reload();
                }}
              >
                Start Fresh
              </Button>
            </div>
          </div>
        )}
        
        {/* Auto-save indicator */}
        <div className="mb-4 text-right">
          <span className="text-xs text-gray-500 flex items-center justify-end gap-1">
            <Save className="h-3 w-3" />
            Auto-save enabled
          </span>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isUpgradeMode ? 'Verify Your GST' : 'Vendor Tax Registration'}
          </h1>
          <p className="text-gray-600">
            {isUpgradeMode 
              ? 'Verify your GSTIN to unlock Pan-India selling capabilities' 
              : 'Complete your tax registration to start selling on the marketplace'}
          </p>
          {!isUpgradeMode && (
            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700">
                <strong>Required:</strong> This registration is mandatory for all vendors as per Indian e-commerce regulations. 
                You&apos;ll need your PAN, GST/Enrollment ID, and bank details.
              </p>
            </div>
          )}
        </div>

        {/* Progress Steps */}
        <Card className="mb-8">
          <CardContent className="p-4 sm:p-6">
            {/* Mobile: Compact horizontal scroll */}
            <div className="flex items-center justify-between overflow-x-auto gap-2 sm:gap-0">
              {steps.map((step, index) => (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-shrink-0 min-w-[60px] sm:min-w-0">
                    <div className={`
                      w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2
                      ${currentStep > step.id ? 'bg-green-600 border-green-600' : 
                        currentStep === step.id ? 'bg-blue-600 border-blue-600' : 
                        'bg-white border-gray-300'}
                    `}>
                      {currentStep > step.id ? (
                        <Check className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                      ) : (
                        <step.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${currentStep === step.id ? 'text-white' : 'text-gray-400'}`} />
                      )}
                    </div>
                    <span className={`mt-1 sm:mt-2 text-[10px] sm:text-sm font-medium text-center leading-tight ${
                      currentStep === step.id ? 'text-blue-600' : 'text-gray-600'
                    }`}>
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`flex-1 h-0.5 mx-1 sm:mx-4 min-w-[20px] ${
                      currentStep > step.id ? 'bg-green-600' : 'bg-gray-200'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Step Content */}
        <Card className="mb-6">
          <CardContent className="p-4 sm:p-6 md:p-8">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation Buttons - sticky on mobile */}
        <div className="sticky bottom-0 z-10 bg-gray-50 border-t border-gray-200 -mx-4 px-4 py-3 mt-4 flex justify-between items-center md:relative md:border-t-0 md:bg-transparent md:mx-0 md:px-0 md:py-0 md:mt-0">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1 || loading}
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex items-center gap-3">
            {/* Skip button only on Step 6 (Choose Plan) */}
            {currentStep === 6 && (
              <Button
                variant="ghost"
                onClick={handleSkipSubscription}
                disabled={loading}
                className="text-gray-500 hover:text-gray-700"
              >
                I&apos;ll do it later
              </Button>
            )}
            
            <Button
              onClick={handleNext}
              disabled={loading || !canProceed() || (currentStep === 6 && !selectedPlan)}
              className={currentStep === 6 && selectedPlan ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : currentStep === 6 ? (
                selectedPlan ? (
                  <>
                    Start Free Trial
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  'Select a Plan'
                )
              ) : (
                <>
                  Continue
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Store Identity Step Component - NEW
const StoreIdentityStep = ({ storeIdentity, onChange, validation, errors, formData }) => {
  const [showVerificationOptions, setShowVerificationOptions] = useState(false);
  
  // Get legal name from GST or business details
  // Legal Business Name: Prefer trade/brand name over owner's personal legal name
  // GST lgnm = Owner's name (e.g., "ANJU SHARMA")
  // GST tradeNam = Business/Brand name (e.g., "SIMPLY & BLISSFUL PRODUCTS")
  const legalBusinessName = formData?.gstVerified 
    ? (formData.gstTradeName || formData.businessName || formData.gstLegalName)
    : formData?.businessName || '';
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Store className="h-6 w-6 text-blue-600" />
          Store Identity
        </h2>
        <p className="text-gray-600 mb-6">
          Set up your store name and identity. This is how customers will find you.
        </p>
      </div>
      
      {/* Legal Business Name - Read-only, populated from Business Details step */}
      <div className="space-y-2">
        <Label htmlFor="legalName">
          Legal Business Name (For Invoices)
        </Label>
        <Input
          id="legalName"
          value={legalBusinessName}
          disabled
          className="bg-gray-50"
        />
        <p className="text-xs text-gray-500">
          This is your registered business name from step 1. It will appear on Tax Invoices but is hidden from customers.
        </p>
      </div>

      {/* Store Display Name */}
      <div className="space-y-2">
        <Label htmlFor="storeName">
          Store Display Name (Public URL) *
        </Label>
        <div className="relative">
          <Input
            id="storeName"
            placeholder="e.g., Alpha Sneakers"
            value={storeIdentity.storeName}
            onChange={(e) => onChange('storeName', e.target.value)}
            className={errors.storeName || validation.isValid === false ? 'border-red-500' : validation.isValid === true ? 'border-green-500' : ''}
          />
          {validation.checking && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          )}
          {!validation.checking && validation.isValid === true && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Check className="h-4 w-4 text-green-500" />
            </div>
          )}
          {!validation.checking && validation.isValid === false && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <AlertCircle className="h-4 w-4 text-red-500" />
            </div>
          )}
        </div>
        
        {/* Validation Message */}
        {validation.message && (
          <p className={`text-sm ${validation.isValid ? 'text-green-600' : 'text-red-500'}`}>
            {validation.message}
          </p>
        )}
        
        {/* Suggestions */}
        {validation.suggestions && validation.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-xs text-gray-500">Try:</span>
            {validation.suggestions.map((suggestion, i) => (
              <Button
                key={suggestion.id || suggestion.name || `${suggestion.title || "suggestion"}-${i}`}
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => onChange('storeName', suggestion)}
              >
                {suggestion}
              </Button>
            ))}
          </div>
        )}
        
        {errors.storeName && (
          <p className="text-sm text-red-500">{errors.storeName}</p>
        )}
      </div>

      {/* Naming Policy Disclaimer */}
      <Alert className="bg-amber-50 border-amber-200">
        <AlertTriangle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-sm text-amber-800">
          <strong>Important Naming Policy:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li><strong>Lease, Not Ownership:</strong> Store URLs and Display Names are the property of Aniyor and are leased to you.</li>
            <li><strong>Trademark Rights:</strong> You must not use a Registered Brand Name (e.g., &quot;Nike&quot;, &quot;Apple&quot;) unless you are the authorized owner.</li>
            <li><strong>Impersonation:</strong> Names mimicking established legacy stores without proof of ownership will be renamed.</li>
            <li><strong>Right to Modify:</strong> Aniyor reserves the right to rename your store or reclaim this URL if a valid Legal/Trademark claim is received.</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Mandatory Checkbox */}
      <div className="flex items-start space-x-3 p-4 border rounded-lg bg-gray-50">
        <Checkbox
          id="namingPolicy"
          checked={storeIdentity.namingPolicyAccepted}
          onCheckedChange={(checked) => onChange('namingPolicyAccepted', checked)}
          className={errors.namingPolicyAccepted ? 'border-red-500' : ''}
        />
        <div className="grid gap-1.5 leading-none">
          <Label htmlFor="namingPolicy" className="text-sm font-medium cursor-pointer">
            I certify that I have the legal right to use this Store Name and I accept the Aniyor Naming Policy. *
          </Label>
          {errors.namingPolicyAccepted && (
            <p className="text-sm text-red-500">{errors.namingPolicyAccepted}</p>
          )}
        </div>
      </div>

      {/* Verification Upsell */}
      <Card className="border-2 border-dashed">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="h-5 w-5 text-blue-600" />
            Protect Your Brand Identity
          </CardTitle>
          <CardDescription>
            Do you own the Trademark or a Physical Shop for this name? Verify now to get a Badge and protect your name from copycats.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Trademark Option */}
            <div 
              onClick={() => {
                onChange('verificationChoice', 'trademark');
                setShowVerificationOptions(true);
              }}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                storeIdentity.verificationChoice === 'trademark' 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <BadgeCheck className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Blue Tick</span>
              </div>
              <p className="text-xs text-gray-600">I own the Registered Trademark ®</p>
            </div>
            
            {/* Legacy Store Option */}
            <div 
              onClick={() => {
                onChange('verificationChoice', 'legacy_store');
                setShowVerificationOptions(true);
              }}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                storeIdentity.verificationChoice === 'legacy_store' 
                  ? 'border-green-500 bg-green-50' 
                  : 'hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Store className="h-5 w-5 text-green-500" />
                <span className="font-medium">Green Tick</span>
              </div>
              <p className="text-xs text-gray-600">I own a Famous Physical Store</p>
            </div>
            
            {/* Skip Option */}
            <div 
              onClick={() => {
                onChange('verificationChoice', 'skip');
                setShowVerificationOptions(false);
              }}
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                storeIdentity.verificationChoice === 'skip' 
                  ? 'border-gray-500 bg-gray-50' 
                  : 'hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-gray-500" />
                <span className="font-medium">Skip for Now</span>
              </div>
              <p className="text-xs text-gray-600">I am a Reseller / New Business</p>
            </div>
          </div>

          {/* Trademark Fields */}
          {storeIdentity.verificationChoice === 'trademark' && (
            <div className="mt-4 p-4 border rounded-lg bg-blue-50/50 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Brand Name</Label>
                  <Input
                    placeholder="e.g., Nike"
                    value={storeIdentity.brandName}
                    onChange={(e) => onChange('brandName', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">TM Status</Label>
                  <select
                    className="w-full h-9 rounded-md border px-3 text-sm"
                    value={storeIdentity.tmStatus}
                    onChange={(e) => onChange('tmStatus', e.target.value)}
                  >
                    <option value="">Select</option>
                    <option value="REGISTERED">Registered ®</option>
                    <option value="APPLIED">Applied ™</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Trademark Number</Label>
                <Input
                  placeholder="e.g., TM123456"
                  value={storeIdentity.tmNumber}
                  onChange={(e) => onChange('tmNumber', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Certificate URL (Upload to cloud storage)</Label>
                <Input
                  placeholder="https://..."
                  value={storeIdentity.tmCertificateUrl}
                  onChange={(e) => onChange('tmCertificateUrl', e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Legacy Store Fields */}
          {storeIdentity.verificationChoice === 'legacy_store' && (
            <div className="mt-4 p-4 border rounded-lg bg-green-50/50 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Year Established</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 2010"
                    min="1900"
                    max={new Date().getFullYear() - 3}
                    value={storeIdentity.storeEstablishedYear}
                    onChange={(e) => onChange('storeEstablishedYear', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Shop License URL</Label>
                  <Input
                    placeholder="https://..."
                    value={storeIdentity.shopLicenseUrl}
                    onChange={(e) => onChange('shopLicenseUrl', e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Store Address</Label>
                <Textarea
                  placeholder="Full address with city and pincode"
                  value={storeIdentity.storeAddress}
                  onChange={(e) => onChange('storeAddress', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Business Details Step Component with CAPTCHA-based GST verification
const BusinessDetailsStep = ({ 
  formData, 
  onChange, 
  onVerifyGST, 
  onRefreshCaptcha, 
  captchaData, 
  setCaptchaData, 
  onVerifyEnrollment,
  onRefreshEnrollmentCaptcha,
  enrollmentCaptchaData,
  setEnrollmentCaptchaData,
  enrollmentVerificationResult,
  verifying, 
  errors, 
  onboardingProfile 
}) => {
  const isIndividualOrHomepreneur = onboardingProfile?.persona === 'individual' || onboardingProfile?.persona === 'homepreneur';
  const isGstExempted = onboardingProfile?.gst_exempted;
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <Building className="h-6 w-6 text-blue-600" />
          Business Details
        </h2>
        <p className="text-gray-600 mb-4">Provide your GST and business information for tax compliance</p>
      </div>
      
      {/* GST Exemption Notice - if user selected GST-exempted in wizard */}
      {isGstExempted && (
        <Alert className="bg-green-50 border-green-200">
          <Check className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 text-sm">
            <strong>GST-Exempted Seller:</strong> You indicated that you deal only in GST-exempted goods. 
            You can still register with PAN only and sell tax-free items like fresh produce, educational books, 
            healthcare items, and raw agricultural products.
          </AlertDescription>
        </Alert>
      )}

      {/* Enrollment ID Suggestion for Individual/Homepreneur */}
      {isIndividualOrHomepreneur && !isGstExempted && (
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-sm">
            <strong>Tip for New Sellers:</strong> If you don&apos;t have a GST number yet, you can use an 
            <strong> Enrollment ID (EID)</strong> to start selling within your state while your GST application is pending.{' '}
            <a 
              href="https://reg.gst.gov.in/registration/generateuid" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline inline-flex items-center gap-1"
            >
              Apply for Enrollment ID <ExternalLink className="h-3 w-3" />
            </a>
          </AlertDescription>
        </Alert>
      )}
      
      {/* Disclaimer */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 text-sm">
          <strong>Tax Compliance Notice:</strong> Your GST/PAN details are verified through government APIs and 
          used solely for generating compliant invoices. This information is securely stored and never shared 
          with third parties.
        </AlertDescription>
      </Alert>

      {/* GST or No GST — 3 options, stacks on mobile */}
      <div>
        <Label className="mb-3 block">Do you have a GST number?</Label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* ── Yes ── */}
          <label className={`
            flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all
            ${formData.hasGST === 'yes' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
          `}>
            <input
              type="radio"
              name="hasGST"
              value="yes"
              checked={formData.hasGST === 'yes'}
              onChange={(e) => onChange('hasGST', e.target.value)}
              className="mt-1"
            />
            <div className="ml-3">
              <div className="font-medium">Yes — GST Registered</div>
              <div className="text-sm text-gray-600">I have a GSTIN (Tier 3 — Pan India)</div>
            </div>
          </label>
          {/* ── No ── */}
          <label className={`
            flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all
            ${formData.hasGST === 'no' ? 'border-blue-600 bg-blue-50' : 'border-gray-200'}
          `}>
            <input
              type="radio"
              name="hasGST"
              value="no"
              checked={formData.hasGST === 'no'}
              onChange={(e) => onChange('hasGST', e.target.value)}
              className="mt-1"
            />
            <div className="ml-3">
              <div className="font-medium">No — PAN / Enrollment</div>
              <div className="text-sm text-gray-600">Using PAN or Enrollment ID (Tier 1–2)</div>
            </div>
          </label>
          {/* ── GST Exempt (Tier 5) ── */}
          <label className={`
            flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all
            ${formData.hasGST === 'exempt' ? 'border-green-600 bg-green-50' : 'border-gray-200'}
          `}>
            <input
              type="radio"
              name="hasGST"
              value="exempt"
              checked={formData.hasGST === 'exempt'}
              onChange={(e) => onChange('hasGST', e.target.value)}
              className="mt-1"
            />
            <div className="ml-3">
              <div className="font-medium text-green-800">GST Exempt</div>
              <div className="text-sm text-green-700">My supply is exempt by law (Tier 5 — Pan India)</div>
              <div className="text-xs text-gray-500 mt-1">e.g. Doctors, Pujari, Religious books</div>
            </div>
          </label>
        </div>
      </div>

      {/* GST Section with CAPTCHA Verification */}
      {formData.hasGST === 'yes' && (
        <div className="space-y-4">
          <div>
            <Label className={errors.gstin ? 'text-red-600' : ''}>
              GSTIN Number {errors.gstin && <AlertCircle className="inline h-4 w-4 ml-1" />}
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Enter 15-digit GSTIN"
                value={formData.gstin}
                onChange={(e) => onChange('gstin', e.target.value.toUpperCase())}
                maxLength={15}
                className={`flex-1 ${errors.gstin ? 'border-red-500 focus:ring-red-500' : ''}`}
                disabled={formData.gstVerified}
              />
              {!captchaData.showCaptcha && !formData.gstVerified && (
                <Button 
                  onClick={onVerifyGST}
                  disabled={formData.gstin.length !== 15 || verifying || captchaData.loading}
                >
                  {captchaData.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Verify GST'
                  )}
                </Button>
              )}
              {formData.gstVerified && (
                <Button className="bg-green-600 hover:bg-green-700" disabled>
                  <Check className="w-4 h-4 mr-1" />
                  Verified
                </Button>
              )}
            </div>
            {errors.gstin && (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {errors.gstin}
              </p>
            )}
          </div>

          {/* CAPTCHA Section - Shown after clicking Verify */}
          {captchaData.showCaptcha && !formData.gstVerified && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-blue-900">Government Portal Verification</span>
              </div>
              <p className="text-sm text-blue-700">
                To verify your GSTIN, please solve the CAPTCHA below. This verification is done directly with the Government GST Portal.
              </p>
              
              {/* CAPTCHA Image */}
              <div className="flex items-center gap-4">
                <div className="bg-white p-2 rounded border border-gray-300">
                  {captchaData.captchaImage ? (
                    <img 
                      src={captchaData.captchaImage} 
                      alt="CAPTCHA" 
                      className="h-12"
                    />
                  ) : (
                    <div className="h-12 w-32 flex items-center justify-center text-gray-400">
                      Loading...
                    </div>
                  )}
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={onRefreshCaptcha}
                  disabled={captchaData.loading}
                  title="Refresh CAPTCHA"
                >
                  <RefreshCw className={`w-4 h-4 ${captchaData.loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* CAPTCHA Input */}
              <div>
                <Label>Enter CAPTCHA Text</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    placeholder="Enter the text shown above"
                    value={captchaData.captchaInput}
                    onChange={(e) => setCaptchaData(prev => ({ ...prev, captchaInput: e.target.value }))}
                    className="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        onVerifyGST();
                      }
                    }}
                  />
                  <Button 
                    onClick={onVerifyGST}
                    disabled={!captchaData.captchaInput || verifying}
                  >
                    {verifying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Submit'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* GST Verified Details */}
          {formData.gstVerified && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-center mb-3">
                <Check className="w-5 h-5 text-green-600 mr-2" />
                <span className="font-medium text-green-800">GST Details Verified from Government Portal</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm text-green-700">
                <div>
                  <span className="font-medium">Legal Name:</span>
                  <div>{formData.gstLegalName}</div>
                </div>
                {formData.gstTradeName && (
                  <div>
                    <span className="font-medium">Trade Name:</span>
                    <div>{formData.gstTradeName}</div>
                  </div>
                )}
                {formData.gstStatus && (
                  <div>
                    <span className="font-medium">Status:</span>
                    <div className={formData.gstStatus === 'Active' ? 'text-green-700 font-semibold' : 'text-red-600'}>
                      {formData.gstStatus}
                    </div>
                  </div>
                )}
                {formData.gstRegistrationDate && (
                  <div>
                    <span className="font-medium">Registration Date:</span>
                    <div>{formData.gstRegistrationDate}</div>
                  </div>
                )}
                {formData.gstConstitution && (
                  <div>
                    <span className="font-medium">Constitution:</span>
                    <div>{formData.gstConstitution}</div>
                  </div>
                )}
                {formData.gstTaxpayerType && (
                  <div>
                    <span className="font-medium">Taxpayer Type:</span>
                    <div>{formData.gstTaxpayerType}</div>
                  </div>
                )}
              </div>
              {formData.gstBusinessAddress && (
                <div className="mt-3 pt-3 border-t border-green-200 text-sm text-green-700">
                  <span className="font-medium">Registered Address:</span>
                  <div className="mt-1">{formData.gstBusinessAddress}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Enrollment ID Section */}
      {formData.hasGST === 'no' && (
        <div className="space-y-4">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-1">Tier 1 Tax Compliance (PAN Only)</h3>
                <p className="text-sm text-amber-700 mb-2">
                  You can start selling with just your PAN. However, GST registration will be required for full marketplace access and inter-state transactions.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="font-semibold text-blue-900 mb-2">Get Enrollment ID (EID)</h3>
            <p className="text-sm text-blue-700 mb-3">
              Apply for an Enrollment ID from the GST portal to sell within your state
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('https://reg.gst.gov.in/registration/generateuid', '_blank')}
            >
              Apply for EID
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {/* Enrollment ID Input with Verification */}
          <div>
            <Label className={errors.enrollmentId ? 'text-red-600' : ''}>
              Enrollment ID / ARN {errors.enrollmentId && <AlertCircle className="inline h-4 w-4 ml-1" />}
              {formData.enrollmentVerified && <span className="ml-2 text-xs text-green-600 font-normal">(Verified)</span>}
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                placeholder="Enter ARN (e.g., AA0408210000001)"
                value={formData.enrollmentId}
                onChange={(e) => onChange('enrollmentId', e.target.value.toUpperCase())}
                className={`flex-1 ${errors.enrollmentId ? 'border-red-500 focus:ring-red-500' : ''} ${
                  formData.enrollmentVerified ? 'bg-green-50 border-green-300' : ''
                }`}
                disabled={formData.applyEnrollmentLater || formData.enrollmentVerified}
              />
              {!formData.enrollmentVerified && !formData.applyEnrollmentLater && (
                <Button
                  variant="outline"
                  onClick={onVerifyEnrollment}
                  disabled={!formData.enrollmentId || formData.enrollmentId.length < 10 || verifying || enrollmentCaptchaData?.loading}
                >
                  {enrollmentCaptchaData?.loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <ShieldCheck className="w-4 h-4 mr-2" />
                      Verify
                    </>
                  )}
                </Button>
              )}
              {formData.enrollmentVerified && (
                <div className="flex items-center px-3 bg-green-100 text-green-700 rounded-md">
                  <Check className="w-4 h-4 mr-1" />
                  Verified
                </div>
              )}
            </div>
            {errors.enrollmentId && (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {errors.enrollmentId}
              </p>
            )}
          </div>

          {/* Enrollment CAPTCHA Section */}
          {enrollmentCaptchaData?.showCaptcha && !formData.enrollmentVerified && (
            <div className="p-4 border rounded-lg space-y-4 bg-gray-50">
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  To verify your Enrollment ID, please solve the CAPTCHA below. This verification is done directly with the Government GST Portal.
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                {enrollmentCaptchaData.captchaImage ? (
                  <img 
                    src={enrollmentCaptchaData.captchaImage} 
                    alt="CAPTCHA" 
                    className="h-12 border rounded bg-white"
                  />
                ) : (
                  <div className="h-12 w-32 bg-gray-200 rounded flex items-center justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
                  </div>
                )}
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onRefreshEnrollmentCaptcha}
                  disabled={enrollmentCaptchaData.loading}
                >
                  <RefreshCw className={`w-4 h-4 ${enrollmentCaptchaData.loading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              
              <div className="flex gap-2">
                <Input
                  placeholder="Enter CAPTCHA text"
                  value={enrollmentCaptchaData.captchaInput}
                  onChange={(e) => setEnrollmentCaptchaData(prev => ({ ...prev, captchaInput: e.target.value }))}
                  className="flex-1"
                />
                <Button 
                  onClick={onVerifyEnrollment}
                  disabled={!enrollmentCaptchaData.captchaInput || verifying}
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Enrollment Verification Result */}
          {formData.enrollmentVerified && enrollmentVerificationResult?.success && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <div className="flex items-start gap-2 mb-3">
                <BadgeCheck className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <h4 className="font-semibold text-green-800">Enrollment ID Verified Successfully</h4>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm text-green-700">
                {enrollmentVerificationResult.status && (
                  <div>
                    <span className="font-medium">Status:</span>
                    <div className="text-green-800 font-semibold">{enrollmentVerificationResult.status}</div>
                  </div>
                )}
                {enrollmentVerificationResult.applicantName && (
                  <div>
                    <span className="font-medium">Applicant Name:</span>
                    <div>{enrollmentVerificationResult.applicantName}</div>
                  </div>
                )}
                {enrollmentVerificationResult.tradeName && (
                  <div>
                    <span className="font-medium">Trade Name:</span>
                    <div>{enrollmentVerificationResult.tradeName}</div>
                  </div>
                )}
                {enrollmentVerificationResult.applicationDate && (
                  <div>
                    <span className="font-medium">Application Date:</span>
                    <div>{enrollmentVerificationResult.applicationDate}</div>
                  </div>
                )}
                {enrollmentVerificationResult.state && (
                  <div>
                    <span className="font-medium">State:</span>
                    <div>{enrollmentVerificationResult.state}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Apply Later Checkbox */}
          <label className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg cursor-pointer border border-gray-200">
            <Checkbox
              checked={formData.applyEnrollmentLater}
              onCheckedChange={(checked) => {
                onChange('applyEnrollmentLater', checked);
                if (checked) {
                  onChange('enrollmentId', ''); // Clear enrollment ID if applying later
                  onChange('enrollmentVerified', false);
                }
              }}
              disabled={formData.enrollmentVerified}
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">I will apply for Enrollment ID later</span>
              <p className="text-xs text-gray-500 mt-1">
                You can complete registration with PAN only and add Enrollment ID later from your profile settings. You&apos;ll receive reminders to complete this step.
              </p>
            </div>
          </label>
        </div>
      )}

      {/* ── Tier 5: GST Exempt Section ─────────────────────────────────── */}
      {formData.hasGST === 'exempt' && (
        <div className="space-y-4">
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-start gap-2">
              <svg className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              <div>
                <h3 className="font-semibold text-green-900 mb-1">Tier 5 — GST Exempt Seller</h3>
                <p className="text-sm text-green-800 mb-1">
                  Your supplies are exempt under the CGST Act, 2017. You will <strong>NOT</strong> charge GST
                  to customers and are not required to file GST returns.
                </p>
                <ul className="text-xs text-green-700 list-disc list-inside space-y-0.5">
                  <li>Pan-India transactions allowed (same as Tier 3)</li>
                  <li>TDS &amp; TCS still apply on your payouts (PAN-based)</li>
                  <li>No GST liability section on your payout statements</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Exempt Category */}
          <div>
            <Label className={errors.gstExemptCategory ? 'text-red-600' : ''}>
              Exempt Supply Category *{errors.gstExemptCategory && <span className="ml-2 text-xs text-red-500">{errors.gstExemptCategory}</span>}
            </Label>
            <select
              className={`mt-2 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 ${
                errors.gstExemptCategory ? 'border-red-500' : 'border-gray-300'
              }`}
              value={formData.gstExemptCategory}
              onChange={(e) => onChange('gstExemptCategory', e.target.value)}
              data-testid="gst-exempt-category"
            >
              <option value="">— Select exempt category —</option>
              <option value="healthcare">Healthcare &amp; Medical Services (SAC 9993) — Doctors, Clinics, Hospitals</option>
              <option value="religious_services">Religious &amp; Spiritual Services (SAC 9994) — Pujari, Priests, Temple Services</option>
              <option value="religious_goods">Religious / Puja Goods — Books, Idols, Puja Samagri, Rosaries</option>
              <option value="educational">Educational Services (SAC 9992) — Schools, Coaching, Tutoring</option>
              <option value="agricultural">Agricultural Produce — Fruits, Vegetables, Grains, Pulses</option>
              <option value="charitable">Charitable / NGO Services — Registered Charities, Trusts</option>
              <option value="other">Other GST-Exempt Supply — Please describe below</option>
            </select>
          </div>

          {/* Optional reason / description */}
          <div>
            <Label className="text-gray-700">
              Additional description <span className="text-xs font-normal text-gray-400">(optional)</span>
            </Label>
            <Input
              className="mt-2"
              placeholder="e.g. Ayurvedic consultation services, free temple prasad distribution..."
              value={formData.gstExemptReason}
              onChange={(e) => onChange('gstExemptReason', e.target.value)}
              data-testid="gst-exempt-reason"
            />
          </div>

          {/* Declaration Checkbox */}
          <label className={`flex items-start space-x-3 p-3 rounded-lg cursor-pointer border ${
            errors.gstExemptDeclared ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'
          }`}>
            <Checkbox
              checked={formData.gstExemptDeclared}
              onCheckedChange={(checked) => onChange('gstExemptDeclared', !!checked)}
              data-testid="gst-exempt-declared"
            />
            <div className="flex-1">
              <span className="text-sm font-medium text-gray-900">
                I confirm that my primary business involves GST-exempt supplies as per the CGST Act, 2017
              </span>
              <p className="text-xs text-gray-500 mt-1">
                By checking this, you declare that you are not required to collect or remit GST and that
                misrepresentation may result in account suspension.
              </p>
              {errors.gstExemptDeclared && (
                <p className="text-xs text-red-600 mt-1">{errors.gstExemptDeclared}</p>
              )}
            </div>
          </label>
        </div>
      )}

      {/* PAN Number */}
      <div>
        <Label className={errors.panNumber ? 'text-red-600' : ''}>
          PAN Number * {errors.panNumber && <AlertCircle className="inline h-4 w-4 ml-1" />}
          {formData.gstin && formData.gstin.length >= 12 && formData.panNumber && (
            <span className="ml-2 text-xs text-blue-600 font-normal">(Auto-filled from GST)</span>
          )}
        </Label>
        <Input
          placeholder="Enter PAN (e.g., ABCDE1234F)"
          value={formData.panNumber}
          onChange={(e) => onChange('panNumber', e.target.value.toUpperCase())}
          maxLength={10}
          className={`mt-2 ${errors.panNumber ? 'border-red-500 focus:ring-red-500' : ''} ${
            formData.gstin && formData.gstin.length >= 12 && formData.panNumber ? 'bg-blue-50 border-blue-200' : ''
          }`}
        />
        {errors.panNumber ? (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {errors.panNumber}
          </p>
        ) : formData.panNumber && !formData.panValid ? (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            Invalid PAN format (must be like ABCDE1234F)
          </p>
        ) : formData.panValid ? (
          <p className="text-sm text-green-600 mt-1 flex items-center">
            <Check className="w-4 h-4 mr-1" />
            Valid PAN format
          </p>
        ) : null}
      </div>

      {/* iter-233-F — Personal PAN (required for GST users / T3 & T4) */}
      {formData.hasGST === 'yes' && (
        <div data-testid="onboarding-personal-pan-block">
          <Label className={errors.personalPan ? 'text-red-600' : ''}>
            Personal PAN (Director/Proprietor) * {errors.personalPan && <AlertCircle className="inline h-4 w-4 ml-1" />}
          </Label>
          <Input
            placeholder="Enter individual PAN (4th char must be 'P', e.g., ABCPD1234E)"
            value={formData.personalPan}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              onChange('personalPan', v);
              onChange('personalPanValid', /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(v) && v[3] === 'P');
            }}
            maxLength={10}
            data-testid="onboarding-personal-pan-input"
            className={`mt-2 ${errors.personalPan ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-1">
            For sole proprietors this is usually the same as your Business PAN above. For Pvt Ltd / LLP / Partnership, enter the director's / proprietor's individual PAN. Used by Razorpay for payouts.
          </p>
          {errors.personalPan ? (
            <p className="text-sm text-red-600 mt-1 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {errors.personalPan}
            </p>
          ) : formData.personalPanValid ? (
            <p className="text-sm text-green-600 mt-1 flex items-center">
              <Check className="w-4 h-4 mr-1" />
              Valid Individual PAN
            </p>
          ) : null}
        </div>
      )}

      {/* iter-233-F — IEC Code (optional; promotes Tier 3 → Tier 4 exporter) */}
      {formData.hasGST === 'yes' && (
        <div data-testid="onboarding-iec-block">
          <Label className={errors.iecCode ? 'text-red-600' : ''}>
            IEC Code <span className="text-xs text-gray-500 font-normal">(Optional — required for international exports)</span>
          </Label>
          <Input
            placeholder="Enter 10-character DGFT Import-Export Code"
            value={formData.iecCode}
            onChange={(e) => onChange('iecCode', e.target.value.toUpperCase())}
            maxLength={14}
            data-testid="onboarding-iec-input"
            className={`mt-2 ${errors.iecCode ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
          <p className="text-xs text-gray-500 mt-1">
            Import-Export Code issued by DGFT (Director General of Foreign Trade). Adding an IEC promotes your account to Tier-4 and enables international shipping eligibility.
          </p>
          {errors.iecCode && (
            <p className="text-sm text-red-600 mt-1 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1" />
              {errors.iecCode}
            </p>
          )}
        </div>
      )}

      {/* Business Name */}
      <div>
        <Label className={errors.businessName ? 'text-red-600' : ''}>
          Business Name * {errors.businessName && <AlertCircle className="inline h-4 w-4 ml-1" />}
        </Label>
        <Input
          placeholder="Enter business name"
          value={formData.businessName}
          onChange={(e) => onChange('businessName', e.target.value)}
          className={`mt-2 ${errors.businessName ? 'border-red-500 focus:ring-red-500' : ''}`}
        />
        {errors.businessName && (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {errors.businessName}
          </p>
        )}
      </div>

      {/* Company / Entity Type (canonical SSOT) */}
      <div>
        <Label>Company / Entity Type</Label>
        <select
          value={formData.companyType || ''}
          onChange={(e) => onChange('companyType', e.target.value)}
          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="onboarding-company-type-select"
        >
          <option value="">Select entity type…</option>
          <option value="sole_proprietorship">Sole Proprietorship</option>
          <option value="partnership">Partnership Firm</option>
          <option value="llp">Limited Liability Partnership (LLP)</option>
          <option value="private_limited">Private Limited Company (Pvt Ltd)</option>
          <option value="public_limited">Public Limited Company</option>
          <option value="opc">One Person Company (OPC)</option>
          <option value="huf">Hindu Undivided Family (HUF)</option>
          <option value="trust_ngo">Trust / NGO / Section 8</option>
          <option value="foreign_entity">Foreign Entity</option>
          <option value="other">Other</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Used on tax invoices and for compliance reporting.</p>
      </div>

      {/* Annual Turnover (canonical SSOT) */}
      <div>
        <Label>Annual Turnover (Approx.)</Label>
        <select
          value={formData.annualTurnover || ''}
          onChange={(e) => onChange('annualTurnover', e.target.value)}
          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="onboarding-annual-turnover-select"
        >
          <option value="">Select turnover range…</option>
          <option value="lt_20l">Below ₹20 Lakhs</option>
          <option value="20l_40l">₹20 Lakhs – ₹40 Lakhs</option>
          <option value="40l_75l">₹40 Lakhs – ₹75 Lakhs</option>
          <option value="75l_1cr">₹75 Lakhs – ₹1 Crore</option>
          <option value="1cr_2cr">₹1 Crore – ₹2 Crore</option>
          <option value="2cr_5cr">₹2 Crore – ₹5 Crore</option>
          <option value="gt_5cr">Above ₹5 Crore</option>
        </select>
        <p className="text-xs text-gray-500 mt-1">Helps determine GST thresholds and applicable compliance tier.</p>
      </div>
    </div>
  );
};

// Pickup Address Step Component - Auto-fill from GST address
const PickupAddressStep = ({ formData, onChange, errors, isPickupAddress = true }) => {
  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
  ];

  // Auto-fill from GST address on first render if fields are empty
  useEffect(() => {
    const parsed = formData.gstParsedAddress;
    if (parsed && !formData.addressPrefilled) {
      // Only auto-fill if address fields are empty
      const isEmptyAddress = !formData.roomNumber && !formData.streetLocality && !formData.city;
      
      if (isEmptyAddress) {
        // Pre-fill individual fields from parsed GST address
        if (parsed.building_name || parsed.floor_number) {
          const buildingParts = [parsed.floor_number ? `Floor ${parsed.floor_number}` : '', parsed.building_name].filter(Boolean);
          onChange('roomNumber', buildingParts.join(', '));
        }
        if (parsed.street || parsed.locality) {
          onChange('streetLocality', [parsed.street, parsed.locality].filter(Boolean).join(', '));
        }
        if (parsed.city) {
          onChange('city', parsed.city);
        }
        if (parsed.district) {
          onChange('landmark', `Near ${parsed.district}`);
        }
        if (parsed.state) {
          onChange('state', parsed.state);
        }
        if (parsed.pincode) {
          onChange('pincode', parsed.pincode);
        }
        // Mark as prefilled to avoid re-running
        onChange('addressPrefilled', true);
      }
    }
  }, [formData.gstParsedAddress, formData.addressPrefilled, formData.roomNumber, formData.streetLocality, formData.city, onChange]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
          <MapPin className="h-6 w-6 text-blue-600" />
          {isPickupAddress ? 'Pickup Address' : 'Business Address'}
        </h2>
        <p className="text-gray-600 mb-4">
          {isPickupAddress
            ? 'Where should we pick up products for delivery?'
            : 'Your primary business address — used for GST invoicing, compliance filings, and payout KYC.'}
        </p>
      </div>

      {/* Disclaimer */}
      <Alert className="bg-green-50 border-green-200">
        <MapPin className="h-4 w-4 text-green-600" />
        <AlertDescription className="text-green-800 text-sm">
          {isPickupAddress ? (
            <>
              <strong>Logistics Info:</strong> This address will be used by our courier partners for product pickups.
              Ensure someone is available during business hours to hand over packages. You can update this address
              later from your dashboard.
            </>
          ) : (
            <>
              <strong>Compliance Info:</strong> This address appears on your GST invoices and is required for
              payment gateway KYC. You can update it any time from your profile.
            </>
          )}
        </AlertDescription>
      </Alert>

      {/* GST Address Pre-fill Notice */}
      {formData.gstParsedAddress && formData.gstVerified && formData.addressPrefilled && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-700">
            <span className="font-medium">📍 Address pre-filled from your GST registration.</span>{' '}
            <span className="text-blue-600">You can edit any field below if your pickup location is different (e.g., warehouse, dropshipping location).</span>
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Room / Floor / Building</Label>
          <Input
            placeholder="e.g., 101, Corporate Heights"
            value={formData.roomNumber}
            onChange={(e) => onChange('roomNumber', e.target.value)}
            className="mt-2"
          />
        </div>
        <div>
          <Label>Street / Locality</Label>
          <Input
            placeholder="e.g., MG Road"
            value={formData.streetLocality}
            onChange={(e) => onChange('streetLocality', e.target.value)}
            className="mt-2"
          />
        </div>
      </div>

      <div>
        <Label>Landmark</Label>
        <Input
          placeholder="e.g., Near City Mall"
          value={formData.landmark}
          onChange={(e) => onChange('landmark', e.target.value)}
          className="mt-2"
        />
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label className={errors.pincode ? 'text-red-600' : ''}>
            Pincode * {errors.pincode && <AlertCircle className="inline h-4 w-4 ml-1" />}
          </Label>
          <Input
            placeholder="110001"
            value={formData.pincode}
            onChange={(e) => onChange('pincode', e.target.value)}
            maxLength={6}
            className={`mt-2 ${errors.pincode ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
          {errors.pincode && (
            <p className="text-xs text-red-600 mt-1 flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {errors.pincode}
            </p>
          )}
        </div>
        <div>
          <Label className={errors.city ? 'text-red-600' : ''}>
            City * {errors.city && <AlertCircle className="inline h-4 w-4 ml-1" />}
          </Label>
          <Input
            placeholder="City"
            value={formData.city}
            onChange={(e) => onChange('city', e.target.value)}
            className={`mt-2 ${errors.city ? 'border-red-500 focus:ring-red-500' : ''}`}
          />
          {errors.city && (
            <p className="text-xs text-red-600 mt-1 flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {errors.city}
            </p>
          )}
        </div>
        <div>
          <Label className={errors.state ? 'text-red-600' : ''}>
            State * {errors.state && <AlertCircle className="inline h-4 w-4 ml-1" />}
          </Label>
          <select
            value={formData.state}
            onChange={(e) => onChange('state', e.target.value)}
            className={`w-full mt-2 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
              errors.state ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
            }`}
          >
            <option value="">Select State</option>
            {indianStates.map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
          {errors.state && (
            <p className="text-xs text-red-600 mt-1 flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {errors.state}
            </p>
          )}
        </div>
      </div>

      {/* iter-229f — Delivery co-ordinator contact number for the
          pickup rider. Defaults to vendor's mobile so future bulk
          pickup schedules don't fail on missing phone. */}
      <div>
        <Label>
          Delivery co-ordinator contact no. *{' '}
          <span className="text-xs text-gray-500 font-normal">
            (rider will call this number on pickup arrival)
          </span>
        </Label>
        <Input
          type="tel"
          inputMode="numeric"
          maxLength={15}
          placeholder={formData.mobileNumber ? `Default: ${formData.mobileNumber}` : '10-digit mobile'}
          value={formData.deliveryCoordinatorPhone || ''}
          onChange={(e) => onChange('deliveryCoordinatorPhone', e.target.value.replace(/[^\d+]/g, ''))}
          className="mt-2 font-mono"
          data-testid="pickup-rider-phone-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          Leave blank to use your registered mobile (
          <span className="font-mono">{formData.mobileNumber || 'not set'}</span>
          ). You can override per-pickup later from the order page.
        </p>
      </div>
    </div>
  );
};

// Bank Details Step Component
const BankDetailsStep = ({ formData, onChange, onVerifyIFSC, verifying, errors }) => {
  const accountNumbersMatch = formData.accountNumber === formData.confirmAccountNumber;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-blue-600" />
          Bank Details
        </h2>
        <p className="text-gray-600 mb-4">Add your bank account for payouts</p>
      </div>
      
      {/* Disclaimer */}
      <Alert className="bg-purple-50 border-purple-200">
        <Shield className="h-4 w-4 text-purple-600" />
        <AlertDescription className="text-purple-800 text-sm">
          <strong>Payout Security:</strong> Your bank details are encrypted and stored securely. 
          Payouts are processed within 3-5 business days after order delivery confirmation. 
          Platform commission is deducted before settlement.
        </AlertDescription>
      </Alert>

      {formData.bankVerified ? (
        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Check className="w-5 h-5 text-green-600 mr-2" />
              <span className="font-medium text-green-800">Bank Details Verified</span>
            </div>
            {/* iter-230aq — Allow vendor to re-enter bank details and
                re-run penny-drop verification. Resets `bankVerified`
                AND `bankPennyDropAttempted` so the save flow goes
                through the full /api/vendor-bank-management/verify
                penny-drop path again. */}
            <button
              type="button"
              onClick={() => {
                onChange('bankVerified', false);
                onChange('bankPennyDropAttempted', false);
              }}
              className="text-sm text-purple-700 hover:text-purple-900 underline font-medium"
              data-testid="bank-details-edit-button"
            >
              Edit
            </button>
          </div>
          <div className="space-y-2 text-sm">
            <div><strong>Account Holder:</strong> {formData.accountHolderName}</div>
            <div><strong>Account Number:</strong> {formData.accountNumber}</div>
            <div><strong>IFSC Code:</strong> {formData.ifscCode}</div>
            <div><strong>Bank:</strong> {formData.bankName}</div>
            <div><strong>Branch:</strong> {formData.branch}</div>
          </div>
        </div>
      ) : (
        <>
          <div>
            <Label className={errors.accountHolderName ? 'text-red-600' : ''} data-testid="account-holder-label">
              Account Holder Name * {errors.accountHolderName && <AlertCircle className="inline h-4 w-4 ml-1" />}
            </Label>
            <Input
              type="text"
              placeholder="Enter account holder name as per bank records"
              value={formData.accountHolderName}
              onChange={(e) => onChange('accountHolderName', e.target.value)}
              className={`mt-2 ${errors.accountHolderName ? 'border-red-500 focus:ring-red-500' : ''}`}
              data-testid="account-holder-name-input"
            />
            {errors.accountHolderName && (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {errors.accountHolderName}
              </p>
            )}
          </div>

          <div>
            <Label className={errors.accountNumber ? 'text-red-600' : ''}>
              Account Number * {errors.accountNumber && <AlertCircle className="inline h-4 w-4 ml-1" />}
            </Label>
            <Input
              type="text"
              placeholder="Enter account number"
              value={formData.accountNumber}
              onChange={(e) => onChange('accountNumber', e.target.value)}
              className={`mt-2 ${errors.accountNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
              data-testid="account-number-input"
            />
            {errors.accountNumber && (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {errors.accountNumber}
              </p>
            )}
          </div>

          <div>
            <Label className={errors.confirmAccountNumber ? 'text-red-600' : ''}>
              Confirm Account Number * {errors.confirmAccountNumber && <AlertCircle className="inline h-4 w-4 ml-1" />}
            </Label>
            <Input
              type="text"
              placeholder="Re-enter account number"
              value={formData.confirmAccountNumber}
              onChange={(e) => onChange('confirmAccountNumber', e.target.value)}
              className={`mt-2 ${errors.confirmAccountNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
              data-testid="confirm-account-number-input"
            />
            {errors.confirmAccountNumber ? (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {errors.confirmAccountNumber}
              </p>
            ) : formData.confirmAccountNumber && !accountNumbersMatch ? (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                Account numbers do not match
              </p>
            ) : null}
          </div>

          <div>
            <Label className={errors.ifscCode ? 'text-red-600' : ''}>
              IFSC Code * {errors.ifscCode && <AlertCircle className="inline h-4 w-4 ml-1" />}
            </Label>
            <Input
              placeholder="Enter IFSC Code (e.g., HDFC0000001)"
              value={formData.ifscCode}
              onChange={(e) => onChange('ifscCode', e.target.value.toUpperCase())}
              maxLength={11}
              className={`mt-2 ${errors.ifscCode ? 'border-red-500 focus:ring-red-500' : ''}`}
              data-testid="ifsc-code-input"
            />
            {errors.ifscCode && (
              <p className="text-sm text-red-600 mt-1 flex items-center">
                <AlertTriangle className="h-4 w-4 mr-1" />
                {errors.ifscCode}
              </p>
            )}
          </div>

          <Button
            onClick={onVerifyIFSC}
            disabled={
              !formData.accountHolderName ||
              !formData.accountNumber || 
              !accountNumbersMatch || 
              formData.ifscCode.length !== 11 ||
              verifying
            }
            className="w-full"
            data-testid="verify-bank-btn"
          >
            {verifying ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Verifying Bank Details...
              </>
            ) : (
              'Verify Bank Details'
            )}
          </Button>
        </>
      )}
    </div>
  );
};

// Supplier Details Step Component (NEW: with mobile number, WhatsApp, disclaimer, fixed seller agreement link)
const SupplierDetailsStep = ({ formData, onChange, errors, user }) => {
  // Check for data mismatch with GST
  const hasNameMismatch = formData.gstVerified && 
    formData.fullName && 
    formData.gstLegalName && 
    !formData.gstLegalName.toLowerCase().includes(formData.fullName.split(' ')[0].toLowerCase());
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2 flex items-center gap-2">
          <User className="h-6 w-6 text-blue-600" />
          Supplier Details
        </h2>
        <p className="text-gray-600 mb-4">Provide your contact information for business communications</p>
      </div>
      
      {/* Disclaimer */}
      <Alert className="bg-blue-50 border-blue-200">
        <Shield className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800 text-sm">
          <strong>Privacy Notice:</strong> Your contact details will be used for order notifications, 
          customer inquiries, and important platform updates. We will never share your personal 
          information with third parties without your consent.
        </AlertDescription>
      </Alert>

      <div>
        <Label className={errors.fullName ? 'text-red-600' : ''}>
          Full Name * {errors.fullName && <AlertCircle className="inline h-4 w-4 ml-1" />}
        </Label>
        <Input
          placeholder="e.g., Priya Sharma"
          value={formData.fullName}
          onChange={(e) => onChange('fullName', e.target.value)}
          className={`mt-2 ${errors.fullName ? 'border-red-500 focus:ring-red-500' : ''}`}
          data-testid="supplier-full-name-input"
        />
        {errors.fullName && (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {errors.fullName}
          </p>
        )}
        
        {/* Name mismatch warning */}
        {hasNameMismatch && (
          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> The name you entered doesn&apos;t match the GST registered name ({formData.gstLegalName}). 
              This may cause issues with tax compliance. Please ensure accuracy.
            </p>
          </div>
        )}
      </div>
      
      {/* Mobile Number */}
      <div>
        <Label className={errors.mobileNumber ? 'text-red-600' : ''}>
          Mobile Number * {errors.mobileNumber && <AlertCircle className="inline h-4 w-4 ml-1" />}
        </Label>
        <Input
          type="tel"
          placeholder="e.g., 9876543210"
          value={formData.mobileNumber}
          onChange={(e) => onChange('mobileNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
          className={`mt-2 ${errors.mobileNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
          maxLength={10}
          data-testid="supplier-mobile-input"
        />
        {errors.mobileNumber && (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {errors.mobileNumber}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          {user?.mobile ? 'Pre-filled from your account. You can update if needed.' : 'Enter your 10-digit mobile number for order notifications.'}
        </p>
      </div>

      {/* Business WhatsApp Number */}
      <div>
        <Label className={errors.whatsappNumber ? 'text-red-600' : ''}>
          Business WhatsApp Number {errors.whatsappNumber && <AlertCircle className="inline h-4 w-4 ml-1" />}
        </Label>
        <Input
          type="tel"
          placeholder="e.g., 9876543210"
          value={formData.whatsappNumber}
          onChange={(e) => onChange('whatsappNumber', e.target.value.replace(/\D/g, '').slice(0, 10))}
          className={`mt-2 ${errors.whatsappNumber ? 'border-red-500 focus:ring-red-500' : ''}`}
          maxLength={10}
          data-testid="whatsapp-number-input"
        />
        {errors.whatsappNumber && (
          <p className="text-sm text-red-600 mt-1 flex items-center">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {errors.whatsappNumber}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Your business WhatsApp number for receiving order alerts and customer messages. Leave blank to use your mobile number.
        </p>
      </div>

      {/* WhatsApp Opt-in Consent */}
      {(formData.whatsappNumber || formData.mobileNumber) && (
        <label className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer border-2 ${
          formData.whatsappOptIn ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
        }`} data-testid="whatsapp-optin-label">
          <Checkbox
            checked={formData.whatsappOptIn}
            onCheckedChange={(checked) => onChange('whatsappOptIn', checked)}
            data-testid="whatsapp-optin-checkbox"
          />
          <div className="flex-1">
            <span className="text-sm text-gray-700">
              I agree to receive business alerts, order updates, and platform notifications via WhatsApp
            </span>
            <p className="text-xs text-gray-500 mt-1">
              You can opt out at any time from your profile settings. Standard messaging rates may apply.
            </p>
          </div>
        </label>
      )}

      <div>
        <Label>Email</Label>
        <Input
          type="email"
          placeholder="supplier@email.com"
          value={formData.emailId}
          onChange={(e) => onChange('emailId', e.target.value)}
          className="mt-2"
          disabled
          data-testid="supplier-email-input"
        />
        <p className="text-xs text-gray-500 mt-1">Email from your account (cannot be changed here)</p>
      </div>

      <label className={`flex items-start space-x-3 p-4 rounded-lg cursor-pointer border-2 ${
        errors.agreeToTerms ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'
      }`}>
        <Checkbox
          checked={formData.agreeToTerms}
          onCheckedChange={(checked) => onChange('agreeToTerms', checked)}
          data-testid="agree-terms-checkbox"
        />
        <div className="flex-1">
          <span className={`text-sm ${errors.agreeToTerms ? 'text-red-900' : 'text-gray-700'}`}>
            I Agree to the{' '}
            <a 
              href="https://docs.google.com/document/d/1zImfmb0f4lG_pQAbWPztwNO1x2JQHz7yqBP1jsH6W90/edit?usp=sharing" 
              className="text-blue-600 underline font-medium" 
              target="_blank"
              rel="noopener noreferrer"
            >
              Seller Agreement
            </a>{' '}
            and{' '}
            <a 
              href="https://docs.google.com/document/d/1h5g90_NkoNJiqxdBe5LgxyElIka1cF61rHNNIWhg-Ko/edit?usp=sharing" 
              className="text-blue-600 underline font-medium" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
          </span>
          {errors.agreeToTerms && (
            <p className="text-xs text-red-600 mt-1 flex items-center">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {errors.agreeToTerms}
            </p>
          )}
        </div>
      </label>

      {/* WhatsApp Communications Opt-in (Optional, pre-checked) */}
      <label className="flex items-start space-x-3 p-4 rounded-lg cursor-pointer border-2 bg-gray-50 border-gray-200">
        <Checkbox
          checked={formData.whatsappCommunicationsOptIn}
          onCheckedChange={(checked) => onChange('whatsappCommunicationsOptIn', checked)}
          data-testid="whatsapp-comms-checkbox"
        />
        <div className="flex-1">
          <span className="text-sm text-gray-700">
            I agree to receive order updates, important communications & offers via WhatsApp
          </span>
          <p className="text-xs text-gray-400 mt-1">Optional — you can change this anytime from your notification preferences</p>
        </div>
      </label>
    </div>
  );
};

// Subscription Selection Step Component
const SubscriptionSelectionStep = ({ plans, selectedPlan, onSelectPlan, billingCycle, onBillingCycleChange, loading }) => {
  
  const getPlanColor = (planCode, type = 'bg') => {
    const colors = {
      'basic': { bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-500' },
      'free': { bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600', text: 'text-blue-600', light: 'bg-blue-50', border: 'border-blue-500' },
      'pro': { bg: 'bg-purple-600', gradient: 'from-purple-500 to-purple-700', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-500' },
      'professional': { bg: 'bg-purple-600', gradient: 'from-purple-500 to-purple-700', text: 'text-purple-600', light: 'bg-purple-50', border: 'border-purple-500' },
      'business': { bg: 'bg-amber-500', gradient: 'from-amber-400 to-orange-500', text: 'text-amber-600', light: 'bg-amber-50', border: 'border-amber-500' },
      'enterprise': { bg: 'bg-rose-500', gradient: 'from-amber-500 via-orange-500 to-rose-500', text: 'text-rose-600', light: 'bg-rose-50', border: 'border-rose-500' }
    };
    const colorSet = colors[planCode?.toLowerCase()] || colors['basic'];
    return colorSet[type] || colorSet.bg;
  };

  const getPlanIcon = (planCode) => {
    const icons = {
      'basic': <Package className="w-5 h-5" />,
      'free': <Package className="w-5 h-5" />,
      'pro': <Zap className="w-5 h-5" />,
      'professional': <Zap className="w-5 h-5" />,
      'business': <Crown className="w-5 h-5" />,
      'enterprise': <Crown className="w-5 h-5" />
    };
    return icons[planCode?.toLowerCase()] || <Package className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading plans...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
        <p className="text-gray-600">Start with a 30-day free trial. No credit card required.</p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center">
        <div className="relative flex items-center bg-gray-100 p-1 rounded-xl gap-2">
          <div className="relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-green-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap shadow-sm">
              Save 10%
            </div>
            <button 
              type="button"
              onClick={() => onBillingCycleChange('annual')}
              className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
                billingCycle === 'annual' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
            </button>
          </div>
          <button 
            type="button"
            onClick={() => onBillingCycleChange('monthly')}
            className={`px-5 py-2 rounded-lg text-sm font-bold transition-all ${
              billingCycle === 'monthly' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const isSelected = selectedPlan?.id === plan.id;
          const price = billingCycle === 'annual' && plan.annual_price > 0 
            ? Math.round(plan.annual_price / 12) 
            : plan.monthly_price;

          return (
            <div 
              key={plan.id}
              onClick={() => onSelectPlan(plan)}
              className={`relative rounded-xl border-2 transition-all cursor-pointer ${
                isSelected 
                  ? `${getPlanColor(plan.plan_code, 'border')} shadow-lg scale-[1.02] bg-white` 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md bg-white'
              }`}
            >
              {/* Selected Badge */}
              {isSelected && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r ${getPlanColor(plan.plan_code, 'gradient')} text-white text-[10px] uppercase tracking-widest font-bold py-1 px-3 rounded-full shadow`}>
                  Selected
                </div>
              )}

              {/* Popular Badge */}
              {plan.is_featured && !isSelected && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-[10px] uppercase tracking-widest font-bold py-1 px-3 rounded-full shadow">
                  Popular
                </div>
              )}

              <div className="p-4">
                {/* Plan Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-8 h-8 rounded-lg ${getPlanColor(plan.plan_code)} text-white flex items-center justify-center`}>
                    {getPlanIcon(plan.plan_code)}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">{plan.plan_name}</h3>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-4">
                  {plan.is_custom_pricing || price === -1 ? (
                    <span className="text-xl font-black text-gray-900">Custom</span>
                  ) : price === 0 ? (
                    <span className="text-xl font-black text-gray-900">Free</span>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-xl font-black text-gray-900">₹{price.toLocaleString('en-IN')}</span>
                      <span className="text-gray-400 text-sm">/mo</span>
                    </div>
                  )}
                  <p className="text-xs text-gray-400">+GST @18%</p>
                </div>

                {/* Key Features */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className={`w-4 h-4 ${getPlanColor(plan.plan_code, 'text')}`} />
                    <span className="text-gray-600">
                      {plan.max_listings === -1 ? 'Custom' : plan.max_listings === 0 ? 'Unlimited' : plan.max_listings} listings
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`w-4 h-4 ${getPlanColor(plan.plan_code, 'text')}`} />
                    <span className="text-gray-600">
                      {plan.media_storage_mb >= 1024 ? `${(plan.media_storage_mb / 1024).toFixed(0)} GB` : `${plan.media_storage_mb} MB`} storage
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className={`w-4 h-4 ${getPlanColor(plan.plan_code, 'text')}`} />
                    <span className="text-gray-600">{plan.commission_percentage}% commission</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className={`w-4 h-4 ${getPlanColor(plan.plan_code, 'text')}`} />
                    <span className="text-gray-600">30 days free trial</span>
                  </div>
                </div>

                {/* Select indicator */}
                <div className={`mt-4 py-2 text-center rounded-lg text-sm font-medium transition-all ${
                  isSelected 
                    ? `bg-gradient-to-r ${getPlanColor(plan.plan_code, 'gradient')} text-white`
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {isSelected ? '✓ Selected' : 'Click to Select'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Note */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-900">30-Day Free Trial</h4>
            <p className="text-sm text-blue-700 mt-1">
              All plans include a 30-day free trial. No credit card required to start. 
              You can upgrade, downgrade, or cancel anytime from your dashboard.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorTaxRegistration;
